import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { reportsAPI, locationsAPI, usersAPI, filterUserLocations } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogBody,
  DialogFooter,
  DialogTitle,
  DialogDescription,
} from '../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogFooter,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogAction,
  AlertDialogCancel,
} from '../components/ui/alert-dialog';
import { toast } from 'sonner';import { 
  BarChart3, 
  DollarSign, 
  Package, 
  Users,
  TrendingUp,
  AlertTriangle,
  CreditCard,
  Banknote,
  Smartphone,
  Calendar,
  Award,
  FileText,
  ClipboardList,
  Activity,
  Stethoscope,
  RefreshCw,
  Printer,
  ChevronRight,
  ChevronLeft,
  ChevronsLeft,
  ChevronsRight,
  ChevronDown,
  Download,
  ArrowUp,
  ArrowDown,
  Pill,
  History,
  ExternalLink,
  Edit3,
  Lock,
  Unlock,
  UserCheck,
  HelpCircle,
  Paperclip,
  Upload,
  Trash2,
  Mail
} from 'lucide-react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend
} from 'recharts';

// Professional color palette matching blue theme
const CHART_COLORS = ['#1d4ed8', '#2563eb', '#3b82f6', '#60a5fa', '#93c5fd'];


const Reports = () => {
  const { workingLocation, isClinicAdmin, isFrontDesk, clinic, user } = useAuth();
  const navigate = useNavigate();
  const [locations, setLocations] = useState([]);
  const [selectedLocation, setSelectedLocation] = useState('all');
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('closing');
  
  // Check if user has specific locations assigned
  const hasAssignedLocations = user?.location_ids && user.location_ids.length > 0;
  
  // Helper to get local date string in YYYY-MM-DD format
  const getLocalDateString = () => {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Get timezone display name
  const getTimezoneLabel = () => {
    const tz = clinic?.timezone || 'America/Chicago';
    const tzLabels = {
      'America/New_York': 'Eastern Time (ET)',
      'America/Chicago': 'Central Time (CT)',
      'America/Denver': 'Mountain Time (MT)',
      'America/Phoenix': 'Arizona (MST)',
      'America/Los_Angeles': 'Pacific Time (PT)',
      'America/Anchorage': 'Alaska Time (AKT)',
      'Pacific/Honolulu': 'Hawaii Time (HST)',
      'America/Puerto_Rico': 'Atlantic Time (AST)',
    };
    return tzLabels[tz] || tz;
  };

  // Date range for standard reports
  const [dateRange, setDateRange] = useState({
    start: getLocalDateString(),
    end: getLocalDateString()
  });
  
  // Single date for daily closing report
  const [closingDate, setClosingDate] = useState(getLocalDateString());
  
  // Report data
  const [revenueReport, setRevenueReport] = useState(null);
  const [inventoryReport, setInventoryReport] = useState(null);
  const [doctorReport, setDoctorReport] = useState(null);
  const [outstandingReport, setOutstandingReport] = useState(null);
  const [closingReport, setClosingReport] = useState(null);

  // Daily closing revision state
  const [closingRevision, setClosingRevision] = useState(null);
  const [shiftOnShiftIds, setShiftOnShiftIds] = useState([]);
  const [staffList, setStaffList] = useState([]);
  const [revisionDialogOpen, setRevisionDialogOpen] = useState(false);
  const [revisionForm, setRevisionForm] = useState(null);
  const [savingRevision, setSavingRevision] = useState(false);
  const [empSearch, setEmpSearch] = useState('');
  const [uploadingClosingFile, setUploadingClosingFile] = useState(false);
  const [isDraggingClosingFile, setIsDraggingClosingFile] = useState(false);
  const closingFileInputRef = React.useRef(null);
  const [indSearch, setIndSearch] = useState('');

  // Medication Usage Report state
  const [usageReport, setUsageReport] = useState(null);
  const [usageLoading, setUsageLoading] = useState(false);
  const [usageSortBy, setUsageSortBy] = useState('medication_name');
  const [usageSortOrder, setUsageSortOrder] = useState('asc');
  const [usagePage, setUsagePage] = useState(1);
  const [usagePageSize] = useState(15);
  const [expandedMedications, setExpandedMedications] = useState({});
  const [medicationBillHistory, setMedicationBillHistory] = useState({});
  const [billHistoryMeta, setBillHistoryMeta] = useState({});
  const [loadingBillHistory, setLoadingBillHistory] = useState({});
  const [exportingUsage, setExportingUsage] = useState(false);
  
  // Consumption Matrix state - removed, now in Excel export only
  
  // Bonus-JotForm state
  const [jotformData, setJotformData] = useState(null);
  const [jotformLoading, setJotformLoading] = useState(false);
  const [jotformPeriod, setJotformPeriod] = useState({ start: '', end: '' });
  const [expandedJotformRow, setExpandedJotformRow] = useState(null);
  const [jotformPeriodType, setJotformPeriodType] = useState('current');
  const [usageDateRange, setUsageDateRange] = useState(() => {
    const end = new Date();
    const start = new Date();
    start.setDate(start.getDate() - 30);
    return {
      start: start.toISOString().split('T')[0],
      end: end.toISOString().split('T')[0]
    };
  });

  // Closings History state
  const [historyData, setHistoryData] = useState(null);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [expandedClosing, setExpandedClosing] = useState(null);
  const [historyPeriodIndex, setHistoryPeriodIndex] = useState(0);

  // Employee Bonuses (bi-weekly matrix) state
  const [empBonusData, setEmpBonusData] = useState(null);
  const [empBonusLoading, setEmpBonusLoading] = useState(false);
  const [empBonusPeriod, setEmpBonusPeriod] = useState(0);
  const [empBonusView, setEmpBonusView] = useState('net'); // 'net' | 'gross'
  const [emailConfirm, setEmailConfirm] = useState({ open: false, mode: null, emp: null });
  const [sendingPayslipEmail, setSendingPayslipEmail] = useState(false);
  const [expandedEmpBonus, setExpandedEmpBonus] = useState(null);

  useEffect(() => {
    fetchLocations();
  }, []);

  useEffect(() => {
    if (locations.length > 0 || selectedLocation === 'all') {
      if (activeTab === 'closing') {
        fetchClosingReport();
      } else if (activeTab === 'inventory') {
        fetchUsageReport(usageDateRange, usageSortBy, usageSortOrder, 1);
      } else if (activeTab === 'bonus-jotform') {
        fetchBonusJotform();
      } else if (activeTab === 'closings-history') {
        fetchClosingsHistory();
      } else if (activeTab === 'employee-bonuses') {
        fetchEmployeeBonuses();
      } else {
        fetchReports();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateRange, selectedLocation, closingDate, activeTab]);

  useEffect(() => {
    if (activeTab === 'employee-bonuses') fetchEmployeeBonuses();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [empBonusPeriod]);

  const fetchEmployeeBonuses = async () => {
    try {
      setEmpBonusLoading(true);
      const locationId = selectedLocation !== 'all' ? selectedLocation : undefined;
      const res = await reportsAPI.getEmployeeBonuses({ location_id: locationId, period: empBonusPeriod });
      setEmpBonusData(res.data);
    } catch (e) {
      toast.error('Failed to load employee bonuses');
    } finally {
      setEmpBonusLoading(false);
    }
  };

  const empBonusLocationParam = () => (selectedLocation !== 'all' ? selectedLocation : undefined);

  const savePayslipBlob = (blob, filename) => {
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = filename; document.body.appendChild(a); a.click();
    a.remove(); window.URL.revokeObjectURL(url);
  };

  const handleDownloadPayslip = async (emp) => {
    try {
      const res = await reportsAPI.downloadPayslip({ employee_id: emp.employee_id || emp.name, period: empBonusPeriod, location_id: empBonusLocationParam() });
      savePayslipBlob(res.data, `payslip_${(emp.name || 'employee').replace(/\s+/g, '_')}_${empBonusData.period_start}.pdf`);
    } catch (e) {
      toast.error('Failed to download payslip');
    }
  };

  const handleDownloadAllPayslips = async () => {
    try {
      const res = await reportsAPI.downloadAllPayslips({ period: empBonusPeriod, location_id: empBonusLocationParam() });
      savePayslipBlob(res.data, `payslips_all_${empBonusData.period_start}.pdf`);
    } catch (e) {
      toast.error('Failed to download payslips');
    }
  };

  const handleEmailPayslip = async (emp) => {
    setEmailConfirm({ open: true, mode: 'single', emp });
  };

  const handleEmailAllPayslips = async () => {
    setEmailConfirm({ open: true, mode: 'all', emp: null });
  };

  const confirmSendPayslipEmail = async () => {
    const { mode, emp } = emailConfirm;
    setEmailConfirm({ open: false, mode: null, emp: null });
    setSendingPayslipEmail(true);
    try {
      if (mode === 'single') {
        const res = await reportsAPI.emailPayslips({ employee_id: emp.employee_id || emp.name, period: empBonusPeriod, location_id: empBonusLocationParam() });
        if (res.data.queued > 0) toast.success(`Payslip emailed to ${emp.name}`);
        else toast.error('No email on file for this employee');
      } else {
        const res = await reportsAPI.emailPayslips({ period: empBonusPeriod, location_id: empBonusLocationParam() });
        const skipped = res.data.skipped_no_email || [];
        let msg = `Payslips emailed to ${res.data.queued} employee${res.data.queued === 1 ? '' : 's'}`;
        if (skipped.length) msg += ` · ${skipped.length} skipped (no email)`;
        if (res.data.queued > 0) toast.success(msg); else toast.error('No employees with an email on file');
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to email payslip');
    } finally {
      setSendingPayslipEmail(false);
    }
  };

  const fetchClosingsHistory = async () => {
    try {
      setHistoryLoading(true);
      const locationId = selectedLocation !== 'all' ? selectedLocation : undefined;
      const res = await reportsAPI.getClosingsHistory({ location_id: locationId, periods: 24 });
      setHistoryData(res.data.periods || []);
      setHistoryPeriodIndex(0);
      setExpandedClosing(null);
    } catch (e) {
      toast.error('Failed to load closings history');
    } finally {
      setHistoryLoading(false);
    }
  };

  const fetchLocations = async () => {
    try {
      const response = await locationsAPI.getAll({ page_size: 100 });
      const locData = response.data.items || response.data;
      // Filter locations based on user's assigned location_ids
      const allLocations = Array.isArray(locData) ? locData : [];
      const filteredLocations = filterUserLocations(allLocations, user);
      setLocations(filteredLocations);
      
      // If user has assigned locations and current selection is 'all', select the first location
      if (user?.location_ids && user.location_ids.length > 0 && selectedLocation === 'all') {
        if (filteredLocations.length > 0) {
          setSelectedLocation(filteredLocations[0].id);
        }
      }
    } catch (error) {
      console.error('Failed to fetch locations:', error);
    }
  };

  const fetchReports = async () => {
    setLoading(true);
    try {
      const params = {
        start_date: dateRange.start,
        end_date: dateRange.end,
        location_id: selectedLocation !== 'all' ? selectedLocation : undefined
      };
      
      // Always fetch revenue report (available to both clinic_admin and front_desk)
      const revenueRes = await reportsAPI.getRevenue(params);
      setRevenueReport(revenueRes.data);
      
      // Only fetch admin-only reports if user is clinic admin
      if (canSeeAllReports) {
        const [inventoryRes, doctorRes, outstandingRes] = await Promise.all([
          reportsAPI.getInventory(params),
          reportsAPI.getDoctors(params),
          reportsAPI.getOutstanding(params)
        ]);
        setInventoryReport(inventoryRes.data);
        setDoctorReport(doctorRes.data);
        setOutstandingReport(outstandingRes.data);
      }
    } catch (error) {
      toast.error('Failed to fetch reports');
    } finally {
      setLoading(false);
    }
  };

  const fetchClosingReport = async () => {
    setLoading(true);
    try {
      const locationId = selectedLocation !== 'all' ? selectedLocation : undefined;
      const [reportRes, revisionRes] = await Promise.all([
        reportsAPI.getDailyClosing(closingDate, locationId),
        reportsAPI.getClosingRevision(closingDate, locationId)
      ]);
      setClosingReport(reportRes.data);
      setClosingRevision(revisionRes.data.revision || null);
      setShiftOnShiftIds(revisionRes.data.shift_on_shift_ids || []);
    } catch (error) {
      toast.error('Failed to fetch closing report');
    } finally {
      setLoading(false);
    }
  };

  // Load clinic staff for the employee picker (once)
  useEffect(() => {
    (async () => {
      try {
        const res = await reportsAPI.getClosingEmployees();
        setStaffList(res.data.employees || []);
      } catch (e) { /* ignore */ }
    })();
  }, []);

  const BONUS_EXCLUDABLE_ROLES = ['doctor', 'nurse', 'front_desk'];

  const BONUS_TAX_RATE = 0.0765;

  // Compute bonus preview client-side (mirrors backend)
  const computeBonusPreview = (form) => {
    if (!form) return { base: 0, tier: '', perCommon: 0, employees: [], totalGross: 0, totalNet: 0, totalCollected: 0 };
    const pt = form.payment_totals;
    const totalCollected = ['cash', 'credit_card', 'zelle_cashapp', 'other'].reduce((s, k) => s + (parseFloat(pt[k]) || 0), 0);
    const base = totalCollected - (parseFloat(form.medication_excluded_total) || 0) - (parseFloat(form.ekg_total) || 0) - (parseFloat(form.ultrasound_total) || 0);
    let tierBonus = 0, tier = '';
    if (base >= 2000) {
      const steps = Math.floor((base - 2000) / 500);
      tierBonus = 50 + steps * 25;
      tier = `$${(2000 + steps * 500).toLocaleString()}+`;
    }
    const ekgBonus = (parseInt(form.ekg_count) || 0) * 5;
    const perCommon = tierBonus + ekgBonus;
    const onShiftIds = form.on_shift_ids || [];
    const employees = (form.employee_ids || []).map(id => {
      const staff = staffList.find(s => s.id === id);
      const usCt = parseInt(form.us_assignments?.[id]) || 0;
      const onShift = onShiftIds.includes(id);
      const gross = (onShift ? perCommon : 0) + usCt * 5;
      const net = +(gross * (1 - BONUS_TAX_RATE)).toFixed(2);
      return { id, full_name: staff?.full_name || id, role: staff?.role, on_shift: onShift, us_indicated: usCt, us_bonus: usCt * 5, gross_bonus: gross, net_bonus: net };
    });
    const totalGross = employees.reduce((s, e) => s + e.gross_bonus, 0);
    const totalNet = +employees.reduce((s, e) => s + e.net_bonus, 0).toFixed(2);
    return { base, tier, tierBonus, ekgBonus, perCommon, employees, totalGross, totalNet, totalCollected };
  };

  const openRevisionDialog = () => {
    // Pre-fill from existing revision, else from live report
    const src = closingRevision?.adjusted;
    const bd = closingReport?.bonus_breakdown || {};
    // On-shift pre-fill: respect a real saved selection, otherwise auto-use the Front Desk "On Shift Today" list for this location
    const savedEmpIds = closingRevision?.employee_ids;
    const savedOnShift = closingRevision?.adjusted?.on_shift_ids;
    const computedOnShift = (closingRevision?.computed?.employee_bonuses || []).filter(e => e.on_shift).map(e => e.id);
    const hasShift = Array.isArray(shiftOnShiftIds) && shiftOnShiftIds.length > 0;
    const prefillEmpIds = (savedEmpIds && savedEmpIds.length > 0)
      ? savedEmpIds
      : (hasShift ? shiftOnShiftIds : (savedEmpIds ?? []));
    const prefillOnShift = (savedOnShift && savedOnShift.length > 0)
      ? savedOnShift
      : (hasShift ? shiftOnShiftIds : computedOnShift);
    setRevisionForm({
      payment_totals: {
        cash: src?.payment_totals?.cash ?? closingReport?.payment_totals?.cash ?? 0,
        credit_card: src?.payment_totals?.credit_card ?? closingReport?.payment_totals?.credit_card ?? 0,
        zelle_cashapp: 0,
        other: 0,
      },
      ekg_count: src?.ekg_count ?? bd.ekg_count ?? 0,
      us_count: src?.us_count ?? bd.us_count ?? 0,
      medication_excluded_total: src?.medication_excluded_total ?? bd.medication_other_excluded ?? 0,
      ekg_total: src?.ekg_total ?? bd.ekg_excluded ?? 0,
      ultrasound_total: src?.ultrasound_total ?? bd.ultrasound_excluded ?? 0,
      employee_ids: prefillEmpIds,
      us_assignments: closingRevision?.adjusted?.us_assignments ?? {},
      on_shift_ids: prefillOnShift,
      visit_counts: {
        np: src?.visit_counts?.np ?? closingReport?.visit_type_counts?.NP ?? 0,
        nc: src?.visit_counts?.nc ?? closingReport?.visit_type_counts?.NC ?? 0,
        fu: src?.visit_counts?.fu ?? closingReport?.visit_type_counts?.FU ?? 0,
        lr: src?.visit_counts?.lr ?? closingReport?.visit_type_counts?.LR ?? 0,
        in: src?.visit_counts?.in ?? 0,
      },
      attachment: closingRevision?.attachment ?? null,
    });
    setRevisionDialogOpen(true);
  };

  const toggleRevisionEmployee = (id) => {
    setRevisionForm(prev => {
      const has = prev.employee_ids.includes(id);
      const employee_ids = has ? prev.employee_ids.filter(e => e !== id) : [...prev.employee_ids, id];
      const us_assignments = { ...(prev.us_assignments || {}) };
      let on_shift_ids = [...(prev.on_shift_ids || [])];
      if (has) {
        delete us_assignments[id];
        on_shift_ids = on_shift_ids.filter(e => e !== id);
      } else {
        // Newly added employees default to on-shift
        if (!on_shift_ids.includes(id)) on_shift_ids.push(id);
      }
      return { ...prev, employee_ids, us_assignments, on_shift_ids };
    });
  };

  const toggleOnShift = (id) => {
    setRevisionForm(prev => {
      const on_shift_ids = (prev.on_shift_ids || []).includes(id)
        ? (prev.on_shift_ids || []).filter(e => e !== id)
        : [...(prev.on_shift_ids || []), id];
      return { ...prev, on_shift_ids };
    });
  };

  // Step 1: on-shift employees (worked today, get full tier + EKG + any indicated US)
  const toggleOnShiftEmployee = (id) => {
    setRevisionForm(prev => {
      const isOn = (prev.on_shift_ids || []).includes(id);
      if (isOn) {
        const us_assignments = { ...(prev.us_assignments || {}) };
        delete us_assignments[id];
        return {
          ...prev,
          employee_ids: prev.employee_ids.filter(e => e !== id),
          on_shift_ids: (prev.on_shift_ids || []).filter(e => e !== id),
          us_assignments,
        };
      }
      return {
        ...prev,
        employee_ids: prev.employee_ids.includes(id) ? prev.employee_ids : [...prev.employee_ids, id],
        on_shift_ids: [...(prev.on_shift_ids || []), id],
      };
    });
  };

  // Step 2: indication-only employees (not on shift, only get +$5 per indicated US)
  const toggleIndicationEmployee = (id) => {
    setRevisionForm(prev => {
      const selected = prev.employee_ids.includes(id) && !(prev.on_shift_ids || []).includes(id);
      if (selected) {
        const us_assignments = { ...(prev.us_assignments || {}) };
        delete us_assignments[id];
        return { ...prev, employee_ids: prev.employee_ids.filter(e => e !== id), us_assignments };
      }
      return {
        ...prev,
        employee_ids: prev.employee_ids.includes(id) ? prev.employee_ids : [...prev.employee_ids, id],
      };
    });
  };

  const setUsAssignment = (id, val) => {
    setRevisionForm(prev => ({ ...prev, us_assignments: { ...(prev.us_assignments || {}), [id]: val } }));
  };

  const saveRevision = async (finalize) => {
    setSavingRevision(true);
    try {
      const locationId = selectedLocation !== 'all' ? selectedLocation : undefined;
      const payload = {
        date: closingDate,
        location_id: locationId || null,
        payment_totals: {
          cash: parseFloat(revisionForm.payment_totals.cash) || 0,
          credit_card: parseFloat(revisionForm.payment_totals.credit_card) || 0,
          zelle_cashapp: 0,
          other: 0,
        },
        ekg_count: parseInt(revisionForm.ekg_count) || 0,
        us_count: parseInt(revisionForm.us_count) || 0,
        medication_excluded_total: parseFloat(revisionForm.medication_excluded_total) || 0,
        ekg_total: parseFloat(revisionForm.ekg_total) || 0,
        ultrasound_total: parseFloat(revisionForm.ultrasound_total) || 0,
        employee_ids: revisionForm.employee_ids,
        us_assignments: revisionForm.us_assignments || {},
        on_shift_ids: revisionForm.on_shift_ids || [],
        visit_counts: {
          np: parseInt(revisionForm.visit_counts?.np) || 0,
          nc: parseInt(revisionForm.visit_counts?.nc) || 0,
          fu: parseInt(revisionForm.visit_counts?.fu) || 0,
          lr: parseInt(revisionForm.visit_counts?.lr) || 0,
          in: parseInt(revisionForm.visit_counts?.in) || 0,
        },
        finalize: !!finalize,
      };
      const res = await reportsAPI.saveClosingRevision(payload);
      setClosingRevision(res.data.revision);
      setRevisionDialogOpen(false);
      toast.success(finalize ? 'Closing finalized' : 'Revision saved');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save revision');
    } finally {
      setSavingRevision(false);
    }
  };

  const reopenRevision = async () => {
    try {
      const locationId = selectedLocation !== 'all' ? selectedLocation : undefined;
      const res = await reportsAPI.reopenClosingRevision({ date: closingDate, location_id: locationId || null });
      setClosingRevision(res.data.revision);
      toast.success('Closing reopened for editing');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reopen');
    }
  };

  const uploadClosingFile = async (file) => {
    if (!file) return;
    setUploadingClosingFile(true);
    try {
      const locationId = selectedLocation !== 'all' ? selectedLocation : undefined;
      const fd = new FormData();
      fd.append('file', file);
      fd.append('date', closingDate);
      if (locationId) fd.append('location_id', locationId);
      const res = await reportsAPI.uploadClosingFile(fd);
      const attachment = res.data.attachment;
      setRevisionForm(p => (p ? { ...p, attachment } : p));
      setClosingRevision(p => (p ? { ...p, attachment } : { attachment }));
      toast.success('File uploaded');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload file');
    } finally {
      setUploadingClosingFile(false);
    }
  };

  const handleClosingFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (file) e.target.value = '';
    await uploadClosingFile(file);
  };

  const handleClosingFileDrop = async (e) => {
    e.preventDefault();
    setIsDraggingClosingFile(false);
    if (uploadingClosingFile) return;
    const file = e.dataTransfer?.files?.[0];
    await uploadClosingFile(file);
  };

  const viewClosingAttachment = async () => {
    try {
      const locationId = selectedLocation !== 'all' ? selectedLocation : undefined;
      const res = await reportsAPI.getClosingAttachmentUrl(closingDate, locationId);
      if (res.data.url) window.open(res.data.url, '_blank', 'noopener');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to open file');
    }
  };

  const openHistoryAttachment = async (date, locationId) => {
    try {
      const res = await reportsAPI.getClosingAttachmentUrl(date, locationId || undefined);
      if (res.data.url) window.open(res.data.url, '_blank', 'noopener');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to open file');
    }
  };

  const deleteClosingAttachment = async () => {
    try {
      const locationId = selectedLocation !== 'all' ? selectedLocation : undefined;
      await reportsAPI.deleteClosingAttachment(closingDate, locationId);
      setRevisionForm(p => (p ? { ...p, attachment: null } : p));
      setClosingRevision(p => (p ? { ...p, attachment: null } : p));
      toast.success('File removed');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to remove file');
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(value || 0);
  };

  const getLocationChartData = () => {
    if (!revenueReport?.by_location) return [];
    return Object.entries(revenueReport.by_location).map(([name, data]) => ({
      name,
      revenue: data.revenue,
      count: data.count
    }));
  };

  const getPaymentMethodChartData = () => {
    if (!revenueReport?.by_payment_method) return [];
    return Object.entries(revenueReport.by_payment_method).map(([name, value]) => ({
      name: name.replace('_', ' '),
      value
    }));
  };

  const getClosingPaymentChartData = () => {
    if (!closingReport?.payment_totals) return [];
    return [
      { name: 'Cash', value: closingReport.payment_totals.cash },
      { name: 'Card', value: closingReport.payment_totals.credit_card },
      { name: 'Digital', value: closingReport.payment_totals.zelle_cashapp },
      { name: 'Other', value: closingReport.payment_totals.other }
    ].filter(item => item.value > 0);
  };

  const getVisitTypeLabel = (type) => {
    switch(type) {
      case 'FU': return 'Follow Up';
      case 'NP': return 'New Patient';
      case 'NC': return 'New Consultation';
      default: return type;
    }
  };

  const getLocationName = () => {
    if (selectedLocation === 'all') return 'All Locations';
    const loc = locations.find(l => l.id === selectedLocation);
    return loc?.name || 'Unknown';
  };

  const handlePrintReport = () => {
    window.print();
  };

  const handleExportExcel = async () => {
    try {
      const locationId = selectedLocation !== 'all' ? selectedLocation : null;
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const token = localStorage.getItem('clinic_token');
      
      if (!token) {
        toast.error('Please log in to export report');
        return;
      }
      
      // Build URL with query params
      let url = `${API_URL}/api/reports/daily-closing/export?date=${closingDate}`;
      if (locationId) {
        url += `&location_id=${locationId}`;
      }
      
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        throw new Error('Failed to export report');
      }
      
      // Get the blob and create download link
      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      
      // Get filename from response header or generate one
      const contentDisposition = response.headers.get('Content-Disposition');
      let filename = `daily_report_${closingDate}.xlsx`;
      if (contentDisposition) {
        const match = contentDisposition.match(/filename=(.+)/);
        if (match) {
          filename = match[1];
        }
      }
      
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);
      
      toast.success('Report exported successfully!');
    } catch (error) {
      console.error('Export error:', error);
      toast.error('Failed to export report');
    }
  };

  const handleExportPdf = async () => {
    try {
      const locationId = selectedLocation !== 'all' ? selectedLocation : null;
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const token = localStorage.getItem('clinic_token');
      if (!token) {
        toast.error('Please log in to export report');
        return;
      }
      let url = `${API_URL}/api/reports/daily-closing/export-pdf?date=${closingDate}`;
      if (locationId) url += `&location_id=${locationId}`;
      const response = await fetch(url, { method: 'GET', headers: { 'Authorization': `Bearer ${token}` } });
      if (!response.ok) {
        if (response.status === 404) {
          toast.error('Save or finalize the adjusted report before exporting to PDF.');
          return;
        }
        throw new Error('Failed to export PDF');
      }
      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      let filename = `daily_closing_${closingDate}.pdf`;
      const contentDisposition = response.headers.get('Content-Disposition');
      if (contentDisposition) {
        const match = contentDisposition.match(/filename=(.+)/);
        if (match) filename = match[1];
      }
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);
      toast.success('PDF exported successfully!');
    } catch (error) {
      console.error('PDF export error:', error);
      toast.error('Failed to export PDF');
    }
  };

  const canSeeAllReports = isClinicAdmin();

  // Consumption matrix fetch removed - now in Excel export only

  const fetchBonusJotform = async (period = jotformPeriodType) => {
    setJotformLoading(true);
    try {
      const response = await reportsAPI.getBonusJotform(period);
      setJotformData(response.data.data || []);
      setJotformPeriod({ start: response.data.period_start, end: response.data.period_end });
    } catch (error) {
      toast.error('Failed to fetch JotForm bonus data');
    } finally {
      setJotformLoading(false);
    }
  };

  // Medication Usage Report functions
  const fetchUsageReport = async (dr = usageDateRange, sortBy = usageSortBy, sortOrder = usageSortOrder, page = usagePage) => {
    setUsageLoading(true);
    try {
      const params = {
        start_date: new Date(dr.start).toISOString(),
        end_date: new Date(dr.end + 'T23:59:59').toISOString(),
        sort_by: sortBy,
        sort_order: sortOrder,
        page: page,
        page_size: usagePageSize,
        include_zero_usage: true
      };
      if (selectedLocation && selectedLocation !== 'all') {
        params.location_id = selectedLocation;
      } else if (workingLocation?.id) {
        params.location_id = workingLocation.id;
      }
      const response = await reportsAPI.getMedicationUsage(params);
      setUsageReport(response.data);
      setExpandedMedications({});
      setMedicationBillHistory({});
    } catch (error) {
      console.error('Failed to fetch usage report:', error);
      toast.error('Failed to fetch medication usage report');
    } finally {
      setUsageLoading(false);
    }
  };

  const handleUsageSort = (field) => {
    let newOrder = 'desc';
    if (usageSortBy === field) {
      newOrder = usageSortOrder === 'desc' ? 'asc' : 'desc';
    }
    setUsageSortBy(field);
    setUsageSortOrder(newOrder);
    setUsagePage(1);
    fetchUsageReport(usageDateRange, field, newOrder, 1);
  };

  const handleUsagePageChange = (newPage) => {
    setUsagePage(newPage);
    fetchUsageReport(usageDateRange, usageSortBy, usageSortOrder, newPage);
  };

  const toggleMedicationExpand = async (inventoryId) => {
    const isCurrentlyExpanded = expandedMedications[inventoryId];
    setExpandedMedications(prev => ({ ...prev, [inventoryId]: !prev[inventoryId] }));
    
    if (!isCurrentlyExpanded && !medicationBillHistory[inventoryId]) {
      setLoadingBillHistory(prev => ({ ...prev, [inventoryId]: true }));
      try {
        const params = {
          start_date: new Date(usageDateRange.start).toISOString(),
          end_date: new Date(usageDateRange.end + 'T23:59:59').toISOString(),
          page: 1,
          page_size: 10
        };
        if (selectedLocation && selectedLocation !== 'all') {
          params.location_id = selectedLocation;
        } else if (workingLocation?.id) {
          params.location_id = workingLocation.id;
        }
        const response = await reportsAPI.getMedicationBillHistory(inventoryId, params);
        setMedicationBillHistory(prev => ({ ...prev, [inventoryId]: response.data.bill_history }));
        setBillHistoryMeta(prev => ({ ...prev, [inventoryId]: { 
          total_count: response.data.total_count, 
          has_more: response.data.has_more, 
          page: 1 
        }}));
      } catch (error) {
        console.error('Failed to fetch bill history:', error);
        toast.error('Failed to fetch bill history');
      } finally {
        setLoadingBillHistory(prev => ({ ...prev, [inventoryId]: false }));
      }
    }
  };

  const loadMoreBillHistory = async (inventoryId) => {
    const meta = billHistoryMeta[inventoryId];
    if (!meta || !meta.has_more) return;
    setLoadingBillHistory(prev => ({ ...prev, [inventoryId]: true }));
    try {
      const nextPage = meta.page + 1;
      const params = {
        start_date: new Date(usageDateRange.start).toISOString(),
        end_date: new Date(usageDateRange.end + 'T23:59:59').toISOString(),
        page: nextPage,
        page_size: 10
      };
      if (selectedLocation && selectedLocation !== 'all') {
        params.location_id = selectedLocation;
      } else if (workingLocation?.id) {
        params.location_id = workingLocation.id;
      }
      const response = await reportsAPI.getMedicationBillHistory(inventoryId, params);
      setMedicationBillHistory(prev => ({
        ...prev,
        [inventoryId]: [...(prev[inventoryId] || []), ...response.data.bill_history]
      }));
      setBillHistoryMeta(prev => ({ ...prev, [inventoryId]: { 
        total_count: response.data.total_count, 
        has_more: response.data.has_more, 
        page: nextPage 
      }}));
    } catch (error) {
      toast.error('Failed to load more bill history');
    } finally {
      setLoadingBillHistory(prev => ({ ...prev, [inventoryId]: false }));
    }
  };

  const navigateToBill = (billId) => {
    navigate(`/billing?bill=${billId}`);
  };

  const setQuickUsageDateRange = (preset) => {
    const end = new Date();
    const start = new Date();
    switch (preset) {
      case 'last7': start.setDate(start.getDate() - 7); break;
      case 'last30': start.setDate(start.getDate() - 30); break;
      case 'lastMonth':
        start.setMonth(start.getMonth() - 1);
        start.setDate(1);
        end.setDate(0);
        break;
      case 'thisMonth': start.setDate(1); break;
      case 'last90': start.setDate(start.getDate() - 90); break;
      default: break;
    }
    const newRange = { start: start.toISOString().split('T')[0], end: end.toISOString().split('T')[0] };
    setUsageDateRange(newRange);
    setUsagePage(1);
    fetchUsageReport(newRange, usageSortBy, usageSortOrder, 1);
  };

  const handleExportUsage = async () => {
    setExportingUsage(true);
    try {
      const params = {
        start_date: new Date(usageDateRange.start).toISOString(),
        end_date: new Date(usageDateRange.end + 'T23:59:59').toISOString(),
        sort_by: usageSortBy,
        sort_order: usageSortOrder,
        include_zero_usage: true
      };
      if (selectedLocation && selectedLocation !== 'all') {
        params.location_id = selectedLocation;
      } else if (workingLocation?.id) {
        params.location_id = workingLocation.id;
      }
      const response = await reportsAPI.exportMedicationUsage(params);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `medication_usage_${usageDateRange.start}_to_${usageDateRange.end}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Report exported successfully!');
    } catch (error) {
      console.error('Export error:', error);
      toast.error('Failed to export report');
    } finally {
      setExportingUsage(false);
    }
  };

  // Stat Card Component for consistent styling with subtle color accents
  const StatCard = ({ label, value, icon: Icon, highlight = false, accent = false }) => (
    <div className={`p-4 rounded-lg border ${
      highlight 
        ? 'bg-blue-100 text-blue-900 border-blue-300' 
        : accent 
          ? 'bg-indigo-50 border-indigo-200' 
          : 'bg-white border-gray-200'
    }`}>
      <div className="flex items-center justify-between">
        <div>
          <p className={`text-xs uppercase tracking-wide ${
            highlight ? 'text-blue-600' : accent ? 'text-indigo-600' : 'text-gray-500'
          }`}>{label}</p>
          <p className={`text-2xl font-semibold mt-1 ${
            highlight ? 'text-blue-800' : accent ? 'text-indigo-700' : 'text-gray-900'
          }`}>{value}</p>
        </div>
        {Icon && (
          <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
            highlight ? 'bg-blue-200' : accent ? 'bg-indigo-100' : 'bg-gray-100'
          }`}>
            <Icon className={`w-5 h-5 ${
              highlight ? 'text-blue-700' : accent ? 'text-indigo-600' : 'text-gray-600'
            }`} />
          </div>
        )}
      </div>
    </div>
  );

  return (
    <div className="space-y-6 animate-fade-in" data-testid="reports-page">
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <div className="flex items-center justify-between border-b border-slate-200">
          <TabsList className="h-auto w-full justify-start gap-6 rounded-none border-0 bg-transparent p-0">
            <TabsTrigger 
              value="closing" 
              className="rounded-none border-b-2 border-transparent bg-transparent px-1 pb-3 pt-2 text-sm font-medium text-slate-500 shadow-none transition-colors hover:text-slate-800 data-[state=active]:border-[#0972d3] data-[state=active]:bg-transparent data-[state=active]:text-[#0972d3] data-[state=active]:shadow-none"
            >
              <FileText className="w-4 h-4 mr-2" />
              Daily Closing
            </TabsTrigger>
            {canSeeAllReports && (
              <>
                <TabsTrigger 
                  value="inventory" 
                  className="rounded-none border-b-2 border-transparent bg-transparent px-1 pb-3 pt-2 text-sm font-medium text-slate-500 shadow-none transition-colors hover:text-slate-800 data-[state=active]:border-[#0972d3] data-[state=active]:bg-transparent data-[state=active]:text-[#0972d3] data-[state=active]:shadow-none"
                >
                  <Package className="w-4 h-4 mr-2" />
                  Inventory
                </TabsTrigger>
                <TabsTrigger 
                  value="closings-history" 
                  className="rounded-none border-b-2 border-transparent bg-transparent px-1 pb-3 pt-2 text-sm font-medium text-slate-500 shadow-none transition-colors hover:text-slate-800 data-[state=active]:border-[#0972d3] data-[state=active]:bg-transparent data-[state=active]:text-[#0972d3] data-[state=active]:shadow-none"
                  data-testid="closings-history-tab"
                >
                  <ClipboardList className="w-4 h-4 mr-2" />
                  Closings History
                </TabsTrigger>
                <TabsTrigger 
                  value="employee-bonuses" 
                  className="rounded-none border-b-2 border-transparent bg-transparent px-1 pb-3 pt-2 text-sm font-medium text-slate-500 shadow-none transition-colors hover:text-slate-800 data-[state=active]:border-[#0972d3] data-[state=active]:bg-transparent data-[state=active]:text-[#0972d3] data-[state=active]:shadow-none"
                  data-testid="employee-bonuses-tab"
                >
                  <Users className="w-4 h-4 mr-2" />
                  Employee Bonuses
                </TabsTrigger>
                <TabsTrigger 
                  value="bonus-jotform" 
                  className="rounded-none border-b-2 border-transparent bg-transparent px-1 pb-3 pt-2 text-sm font-medium text-slate-500 shadow-none transition-colors hover:text-slate-800 data-[state=active]:border-[#0972d3] data-[state=active]:bg-transparent data-[state=active]:text-[#0972d3] data-[state=active]:shadow-none"
                  data-testid="bonus-jotform-tab"
                >
                  <Award className="w-4 h-4 mr-2" />
                  Bonus-JotForm
                </TabsTrigger>
              </>
            )}
          </TabsList>
        </div>

        {/* ==================== DAILY CLOSING REPORT TAB ==================== */}
        <TabsContent value="closing" className="space-y-5">
          {/* Toolbar (AWS Console style) */}
          <div className="flex flex-col gap-4 rounded-md border border-slate-200 bg-white p-3 shadow-sm sm:flex-row sm:flex-wrap sm:items-end sm:justify-between">
            <div className="flex flex-wrap items-end gap-4">
              <div>
                <Label className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">Date</Label>
                <Input
                  type="date"
                  value={closingDate}
                  onChange={(e) => setClosingDate(e.target.value)}
                  className="mt-1 w-44 rounded-sm border-slate-300 font-mono text-sm focus-visible:ring-[#0073BB]"
                  data-testid="closing-date"
                  max={isFrontDesk() ? new Date().toISOString().split('T')[0] : undefined}
                  min={isFrontDesk() ? new Date().toISOString().split('T')[0] : undefined}
                  disabled={isFrontDesk()}
                />
              </div>
              <div>
                <Label className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">Location</Label>
                <Select value={selectedLocation} onValueChange={setSelectedLocation}>
                  <SelectTrigger className="mt-1 w-48 rounded-sm border-slate-300 text-sm" data-testid="closing-location-filter">
                    <SelectValue placeholder={hasAssignedLocations ? "Select Location" : "All Locations"} />
                  </SelectTrigger>
                  <SelectContent className="bg-white">
                    {!hasAssignedLocations && (
                      <SelectItem value="all">All Locations</SelectItem>
                    )}
                    {locations.map((loc) => (
                      <SelectItem key={loc.id} value={loc.id}>{loc.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <Button
                onClick={handleExportPdf}
                className="rounded-sm bg-[#0073BB] text-white shadow-sm hover:bg-[#005A93] disabled:cursor-not-allowed disabled:opacity-50"
                data-testid="export-pdf-btn"
                disabled={!closingRevision}
                title={!closingRevision ? 'Save or finalize the adjusted report first' : 'Export adjusted report as PDF'}
              >
                <FileText className="mr-2 h-4 w-4" />
                Export PDF
              </Button>
            </div>
          </div>

          {loading ? (
            <div className="flex items-center justify-center h-64">
              <RefreshCw className="w-8 h-8 animate-spin text-[#0073BB]" />
            </div>
          ) : closingReport ? (
            <div className="space-y-5 print:space-y-4" id="closing-report">
              {/* Report Header (AWS style) */}
              <div className="flex flex-col gap-3 border-b border-slate-200 pb-4 sm:flex-row sm:items-end sm:justify-between">
                <div>
                  <div className="mb-1 flex items-center gap-2 text-[11px] font-medium uppercase tracking-wider text-slate-400">
                    <span>Reports</span>
                    <ChevronRight className="h-3 w-3" />
                    <span className="text-slate-500">Daily Closing</span>
                  </div>
                  <h1 className="text-2xl font-semibold tracking-tight text-slate-900">Daily Closing Report</h1>
                  <div className="mt-1 flex items-center gap-2 text-sm text-slate-500">
                    <span className="inline-flex items-center gap-1.5 rounded-sm bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-600">
                      <Package className="h-3 w-3" /> {getLocationName()}
                    </span>
                  </div>
                </div>
                <div className="text-left sm:text-right">
                  <p className="font-mono text-lg font-semibold tabular-nums text-slate-900">
                    {(() => {
                      const [year, month, day] = closingDate.split('-');
                      const date = new Date(year, month - 1, day);
                      return date.toLocaleDateString('en-US', {
                        weekday: 'long',
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric'
                      });
                    })()}
                  </p>
                  <p className="mt-0.5 text-xs font-medium uppercase tracking-wider text-slate-400">{getTimezoneLabel()}</p>
                </div>
              </div>

              {/* Revision / Review & Adjust panel */}
              {(isFrontDesk() || isClinicAdmin()) && (
                <div className="overflow-hidden rounded-md border border-slate-200 bg-white shadow-sm" data-testid="closing-revision-panel">
                  <div className="flex flex-col gap-3 border-b border-slate-200 bg-slate-50/60 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
                    <div className="flex flex-wrap items-center gap-2">
                      {closingRevision ? (
                        closingRevision.status === 'finalized' ? (
                          <span className="inline-flex items-center gap-1.5 rounded-sm border border-emerald-200 bg-emerald-50 px-2.5 py-1 text-xs font-semibold text-emerald-800"><Lock className="h-3.5 w-3.5" /> Finalized Revision</span>
                        ) : (
                          <span className="inline-flex items-center gap-1.5 rounded-sm border border-amber-200 bg-amber-50 px-2.5 py-1 text-xs font-semibold text-amber-800"><Edit3 className="h-3.5 w-3.5" /> Draft Revision</span>
                        )
                      ) : (
                        <span className="inline-flex items-center gap-1.5 rounded-sm border border-slate-200 bg-slate-100 px-2.5 py-1 text-xs font-semibold text-slate-700"><FileText className="h-3.5 w-3.5" /> Original (ClinicFlow)</span>
                      )}
                      <span className="text-sm text-slate-500">
                        {closingRevision ? 'Adjusted numbers below reflect the revision.' : 'End-of-day review lets you adjust totals and assign employee bonuses.'}
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      {closingRevision?.status === 'finalized' ? (
                        isClinicAdmin() ? (
                          <Button size="sm" variant="outline" className="rounded-sm border-slate-300" onClick={reopenRevision} data-testid="reopen-revision-btn">
                            <Unlock className="mr-1.5 h-4 w-4" /> Reopen
                          </Button>
                        ) : (
                          <span className="text-xs text-slate-400">Locked — ask a clinic admin to reopen</span>
                        )
                      ) : (
                        <Button onClick={openRevisionDialog} className="w-full rounded-sm bg-[#0073BB] px-6 py-5 text-base font-semibold text-white shadow-sm transition-shadow hover:bg-[#005A93] hover:shadow-md sm:w-auto" data-testid="review-adjust-btn">
                          <Edit3 className="mr-2 h-5 w-5" /> {closingRevision ? 'Edit Revision' : 'Review & Adjust'}
                        </Button>
                      )}
                    </div>
                  </div>

                  {closingRevision && (
                    <div className="p-4">
                      {/* Key info summary */}
                      <div className="mb-4 grid grid-cols-2 gap-3 sm:grid-cols-4" data-testid="revision-summary-kpis">
                        <div className="rounded-sm border border-slate-200 border-t-2 border-t-[#0073BB] bg-white px-3 py-2.5">
                          <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">Total Collected</p>
                          <p className="font-mono text-lg font-bold tabular-nums text-slate-900" data-testid="revision-total-collected">{formatCurrency(closingRevision.computed?.total_collected)}</p>
                        </div>
                        <div className="rounded-sm border border-slate-200 border-t-2 border-t-slate-300 bg-white px-3 py-2.5">
                          <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">Total Cash</p>
                          <p className="font-mono text-lg font-bold tabular-nums text-slate-900" data-testid="revision-total-cash">{formatCurrency(closingRevision.adjusted?.payment_totals?.cash)}</p>
                        </div>
                        <div className="rounded-sm border border-slate-200 border-t-2 border-t-slate-300 bg-white px-3 py-2.5">
                          <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">Total Credit Card</p>
                          <p className="font-mono text-lg font-bold tabular-nums text-slate-900" data-testid="revision-total-credit">{formatCurrency(closingRevision.adjusted?.payment_totals?.credit_card)}</p>
                        </div>
                        <div className="rounded-sm border border-slate-200 border-t-2 border-t-emerald-500 bg-white px-3 py-2.5">
                          <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">Total Net Bonus</p>
                          <p className="font-mono text-lg font-bold tabular-nums text-emerald-700" data-testid="revision-total-net-bonus">{formatCurrency(closingRevision.computed?.total_bonus_net)}</p>
                        </div>
                      </div>
                      {closingRevision.computed?.employee_bonuses?.length ? (
                        <div className="overflow-x-auto rounded-sm border border-slate-200">
                          <table className="w-full text-sm">
                            <thead><tr className="bg-slate-50 text-left text-[11px] uppercase tracking-wider text-slate-600"><th className="px-3 py-2.5 font-semibold">Employee</th><th className="px-3 py-2.5 font-semibold">Role</th><th className="px-3 py-2.5 font-semibold">On Shift</th><th className="px-3 py-2.5 font-semibold">US Indicated</th><th className="px-3 py-2.5 text-right font-semibold">Gross Bonus</th><th className="px-3 py-2.5 text-right font-semibold">Net (−7.65%)</th></tr></thead>
                            <tbody>
                              {closingRevision.computed.employee_bonuses.map(e => (
                                <tr key={e.id} className="border-t border-slate-100 text-slate-700 hover:bg-slate-50/80">
                                  <td className="px-3 py-2 font-medium text-slate-900">{e.full_name}</td>
                                  <td className="px-3 py-2 capitalize text-slate-500">{e.role?.replace('_', ' ')}</td>
                                  <td className="px-3 py-2">{e.on_shift ? <span className="font-medium text-emerald-600">Yes</span> : <span className="text-slate-400">Indication only</span>}</td>
                                  <td className="px-3 py-2">{e.us_indicated > 0 ? <span className="font-mono tabular-nums">{e.us_indicated} <span className="text-xs text-emerald-600">(+{formatCurrency(e.us_bonus)})</span></span> : <span className="text-slate-300">—</span>}</td>
                                  <td className="px-3 py-2 text-right font-mono font-semibold tabular-nums text-slate-900">{formatCurrency(e.gross_bonus)}</td>
                                  <td className="px-3 py-2 text-right font-mono font-semibold tabular-nums text-emerald-700">{formatCurrency(e.net_bonus)}</td>
                                </tr>
                              ))}
                            </tbody>
                            <tfoot><tr className="border-t border-slate-200 bg-slate-50 font-bold"><td className="px-3 py-2.5">Total ({closingRevision.computed.num_employees})</td><td></td><td></td><td></td><td className="px-3 py-2.5 text-right font-mono tabular-nums text-slate-900">{formatCurrency(closingRevision.computed.total_bonus)}</td><td className="px-3 py-2.5 text-right font-mono tabular-nums text-emerald-700">{formatCurrency(closingRevision.computed.total_bonus_net)}</td></tr></tfoot>
                          </table>
                        </div>
                      ) : <p className="text-sm text-slate-400">No employees assigned yet.</p>}
                      <p className="mt-2 text-xs text-slate-400">Bonus base <span className="font-mono">{formatCurrency(closingRevision.computed?.base_amount)}</span> · Tier {closingRevision.computed?.bonus_tier || 'None'} · Base+EKG per employee <span className="font-mono">{formatCurrency(closingRevision.computed?.per_employee_common)}</span></p>
                    </div>
                  )}
                  {closingRevision?.original_snapshot && (
                    <p className="border-t border-slate-100 px-4 py-2.5 text-xs text-slate-400">
                      Original ClinicFlow total collected: <span className="font-mono">{formatCurrency(closingRevision.original_snapshot.total_collected)}</span> · bonus <span className="font-mono">{formatCurrency(closingRevision.original_snapshot.bonus)}</span> (kept for reference)
                    </p>
                  )}
                </div>
              )}

              {/* Review & Adjust Dialog */}
              <Dialog open={revisionDialogOpen} onOpenChange={setRevisionDialogOpen}>
                <DialogContent className="max-w-3xl max-h-[90vh] overflow-hidden flex flex-col p-0 gap-0">
                  <DialogHeader className="border-b border-slate-200 bg-slate-50/60 px-6 py-4">
                    <DialogTitle className="text-lg font-semibold text-slate-900">Review &amp; Adjust — Daily Closing</DialogTitle>
                    <DialogDescription className="text-sm text-slate-500">Adjust the numbers below. The bonus recalculates automatically and the original ClinicFlow data is always kept.</DialogDescription>
                  </DialogHeader>
                  {revisionForm && (() => {
                    const preview = computeBonusPreview(revisionForm);
                    const setPT = (k, v) => setRevisionForm(p => ({ ...p, payment_totals: { ...p.payment_totals, [k]: v } }));
                    const SectionLabel = ({ children }) => (
                      <div className="flex items-center gap-3">
                        <div className="flex items-center gap-2">
                          <span className="h-3.5 w-1 rounded-full bg-[#0073BB]" />
                          <h4 className="whitespace-nowrap text-[11px] font-semibold uppercase tracking-wider text-slate-600">{children}</h4>
                        </div>
                        <div className="h-px flex-1 bg-slate-200" />
                      </div>
                    );
                    return (
                      <>
                        <DialogBody className="overflow-y-auto space-y-6 px-6 py-5">
                          {/* Payment Totals */}
                          <div className="space-y-3">
                            <SectionLabel>Payment Totals</SectionLabel>
                            <div className="grid grid-cols-2 gap-3">
                              {[
                                { k: 'cash', label: 'Cash', Icon: Banknote, testid: 'rev-cash', tint: 'bg-emerald-50 text-emerald-600', top: 'border-t-emerald-400' },
                                { k: 'credit_card', label: 'Credit / Debit Card', Icon: CreditCard, testid: 'rev-card', tint: 'bg-blue-50 text-[#0073BB]', top: 'border-t-[#0073BB]' },
                              ].map(({ k, label, Icon, testid, tint, top }) => (
                                <div key={k} className={`rounded-sm border border-x border-b border-slate-200 border-t-2 ${top} bg-white px-3 py-2 transition-colors focus-within:border-[#0073BB]`}>
                                  <div className="mb-1 flex items-center gap-1.5">
                                    <span className={`flex h-5 w-5 items-center justify-center rounded-sm ${tint}`}><Icon className="h-3 w-3" /></span>
                                    <Label className="text-[11px] font-medium text-slate-500">{label}</Label>
                                  </div>
                                  <div className="flex items-baseline gap-1">
                                    <span className="text-base text-slate-400">$</span>
                                    <Input type="number" step="0.01" value={revisionForm.payment_totals[k]} onChange={e => setPT(k, e.target.value)} className="h-8 border-0 px-0 font-mono text-2xl font-bold tabular-nums shadow-none focus-visible:ring-0" data-testid={testid} />
                                  </div>
                                </div>
                              ))}
                            </div>
                            <div className="flex items-center justify-between rounded-sm border border-emerald-200 bg-emerald-50 px-4 py-2.5">
                              <span className="text-[11px] font-semibold uppercase tracking-wider text-emerald-700">Total Collected</span>
                              <span className="font-mono text-lg font-bold tabular-nums text-emerald-700" data-testid="rev-total-collected">{formatCurrency(preview.totalCollected)}</span>
                            </div>
                          </div>

                          {/* Visit Counts (informational) */}
                          <div className="space-y-3">
                            <SectionLabel>Visit Counts</SectionLabel>
                            <div className="grid grid-cols-5 gap-2">
                              {[
                                { k: 'np', label: 'NP', color: 'text-emerald-600', top: 'border-t-emerald-500' },
                                { k: 'nc', label: 'NC', color: 'text-violet-600', top: 'border-t-violet-500' },
                                { k: 'fu', label: 'FU', color: 'text-[#0073BB]', top: 'border-t-[#0073BB]' },
                                { k: 'lr', label: 'LR', color: 'text-amber-600', top: 'border-t-amber-500' },
                                { k: 'in', label: 'IN', color: 'text-rose-600', top: 'border-t-rose-500' },
                              ].map(({ k, label, color, top }) => (
                                <div key={k} className={`rounded-sm border border-slate-200 border-t-2 ${top} bg-white px-2 py-2 text-center focus-within:border-[#0073BB]`}>
                                  <Label className={`text-[11px] font-bold ${color}`}>{label}</Label>
                                  <Input
                                    type="number"
                                    min="0"
                                    value={revisionForm.visit_counts?.[k] ?? 0}
                                    onChange={e => { const v = e.target.value; setRevisionForm(p => ({ ...p, visit_counts: { ...(p.visit_counts || {}), [k]: v } })); }}
                                    className={`mt-1 h-9 border-0 px-0 text-center font-mono text-2xl font-bold tabular-nums shadow-none focus-visible:ring-0 ${color}`}
                                    data-testid={`rev-vc-${k}`}
                                  />
                                </div>
                              ))}
                            </div>
                            <p className="text-[11px] text-slate-400">
                              <span className="font-bold text-emerald-600">NP</span> = New Patients · <span className="font-bold text-violet-600">NC</span> = New Consultation · <span className="font-bold text-[#0073BB]">FU</span> = Follow Up · <span className="font-bold text-amber-600">LR</span> = Lab Results · <span className="font-bold text-rose-600">IN</span> = Insurance (manual). Informational only — does not affect the bonus.
                            </p>
                          </div>

                          {/* Services + Excluded Revenue */}
                          <div className="space-y-3">
                            <SectionLabel>Services &amp; Excluded Revenue</SectionLabel>
                            <div className="grid grid-cols-2 gap-3">
                              <div className="flex items-center justify-between rounded-sm border border-slate-200 bg-white px-4 py-3 focus-within:border-[#0073BB]">
                                <div className="flex items-center gap-2">
                                  <span className="flex h-7 w-7 items-center justify-center rounded-sm bg-rose-50 text-rose-500"><Activity className="h-4 w-4" /></span>
                                  <Label className="text-xs font-medium text-slate-600">EKG Count</Label>
                                </div>
                                <Input type="number" min="0" value={revisionForm.ekg_count} onChange={e => { const c = e.target.value; setRevisionForm(p => ({ ...p, ekg_count: c, ekg_total: (parseInt(c) || 0) * 100 })); }} className="h-9 w-20 border-0 px-0 text-right font-mono text-3xl font-bold tabular-nums shadow-none focus-visible:ring-0" data-testid="rev-ekg-count" />
                              </div>
                              <div className="flex items-center justify-between rounded-sm border border-slate-200 bg-white px-4 py-3 focus-within:border-[#0073BB]">
                                <div className="flex items-center gap-2">
                                  <span className="flex h-7 w-7 items-center justify-center rounded-sm bg-indigo-50 text-indigo-500"><Stethoscope className="h-4 w-4" /></span>
                                  <Label className="text-xs font-medium text-slate-600">Ultrasound Count</Label>
                                </div>
                                <Input type="number" min="0" value={revisionForm.us_count} onChange={e => { const c = e.target.value; setRevisionForm(p => ({ ...p, us_count: c, ultrasound_total: (parseInt(c) || 0) * 200 })); }} className="h-9 w-20 border-0 px-0 text-right font-mono text-3xl font-bold tabular-nums shadow-none focus-visible:ring-0" data-testid="rev-us-count" />
                              </div>
                            </div>
                            {(() => {
                              const showEkgRev = (parseInt(revisionForm.ekg_count) || 0) > 0;
                              const showUsRev = (parseInt(revisionForm.us_count) || 0) > 0;
                              return (
                                <div className="rounded-sm border border-slate-200 bg-slate-50/50 p-3">
                                  <p className="mb-2 text-xs text-slate-500">Revenue excluded from the bonus base (subtracted before the tier is calculated).</p>
                                  <div className="grid grid-cols-3 gap-3">
                                    <div>
                                      <Label className="text-[11px] font-medium text-slate-500">Meds (Pharmacy)</Label>
                                      <Input type="number" step="0.01" value={revisionForm.medication_excluded_total} onChange={e => setRevisionForm(p => ({ ...p, medication_excluded_total: e.target.value }))} className="mt-1 h-8 rounded-sm border-slate-300 font-mono text-sm tabular-nums" data-testid="rev-med-excl" />
                                    </div>
                                    {showEkgRev && (
                                      <div>
                                        <Label className="text-[11px] font-medium text-slate-500">EKG Revenue</Label>
                                        <Input type="number" step="0.01" value={revisionForm.ekg_total} onChange={e => setRevisionForm(p => ({ ...p, ekg_total: e.target.value }))} className="mt-1 h-8 rounded-sm border-slate-300 font-mono text-sm tabular-nums" data-testid="rev-ekg-total" />
                                      </div>
                                    )}
                                    {showUsRev && (
                                      <div>
                                        <Label className="text-[11px] font-medium text-slate-500">US Revenue</Label>
                                        <Input type="number" step="0.01" value={revisionForm.ultrasound_total} onChange={e => setRevisionForm(p => ({ ...p, ultrasound_total: e.target.value }))} className="mt-1 h-8 rounded-sm border-slate-300 font-mono text-sm tabular-nums" data-testid="rev-us-total" />
                                      </div>
                                    )}
                                  </div>
                                </div>
                              );
                            })()}
                          </div>

                          {/* Employees — Step 1: on-shift, Step 2: indication-only */}
                          <div className="space-y-4">
                            <div className="flex items-center gap-3">
                              <h4 className="whitespace-nowrap text-[11px] font-semibold uppercase tracking-wider text-slate-500">Employee Bonuses</h4>
                              <span className="group relative inline-flex">
                                <button type="button" className="inline-flex items-center gap-1 text-xs font-medium text-[#0073BB] hover:text-[#005A93]" data-testid="rev-tier-tooltip-trigger">
                                  <HelpCircle className="h-3.5 w-3.5" /> How the bonus scales
                                </button>
                                <span className="pointer-events-none absolute bottom-full left-0 z-50 mb-2 hidden w-72 rounded-sm bg-slate-900 p-3 text-xs leading-relaxed text-white shadow-lg group-hover:block" data-testid="rev-tier-tooltip">
                                  <span className="mb-1 block font-semibold">How the bonus scales</span>
                                  Base <span className="font-semibold">$2,000 = $50</span>, then <span className="font-semibold">+$25 for every extra $500</span>. Each EKG adds <span className="font-semibold">+$5</span> and each indicated ultrasound adds <span className="font-semibold">+$5</span>.
                                  <span className="mt-1 block text-slate-300">$2K=$50 · $2.5K=$75 · $3K=$100 · $3.5K=$125 …</span>
                                </span>
                              </span>
                              <div className="h-px flex-1 bg-slate-200" />
                            </div>

                            {/* Step 1: On-shift employees */}
                            <div className="overflow-hidden rounded-sm border border-slate-200">
                              <div className="flex flex-wrap items-center gap-2 border-b border-slate-200 bg-slate-50/60 px-3 py-2">
                                <span className="flex h-5 w-5 items-center justify-center rounded-full bg-[#0073BB] text-[10px] font-bold text-white">1</span>
                                <span className="text-xs font-semibold text-slate-700">On-shift employees</span>
                                <span className="text-[11px] text-slate-400">worked today — get full tier + EKG (plus any US they indicated)</span>
                              </div>
                              <div className="space-y-2 p-3">
                                <Input
                                  placeholder="Search employees..."
                                  value={empSearch}
                                  onChange={e => setEmpSearch(e.target.value)}
                                  className="h-9 rounded-sm border-slate-300"
                                  data-testid="rev-emp-search"
                                />
                                <div className="max-h-44 divide-y divide-slate-100 overflow-y-auto rounded-sm border border-slate-200">
                                  {staffList.length === 0 ? (
                                    <p className="p-3 text-sm text-slate-400">No employees found. Add them in the Employees page.</p>
                                  ) : staffList
                                    .filter(s => (!empSearch || (s.full_name || '').toLowerCase().includes(empSearch.toLowerCase())))
                                    .filter(s => !(revisionForm.employee_ids.includes(s.id) && !(revisionForm.on_shift_ids || []).includes(s.id)))
                                    .map(s => {
                                      const onShift = (revisionForm.on_shift_ids || []).includes(s.id);
                                      return (
                                        <div key={s.id} className={`flex flex-wrap items-center justify-between gap-2 px-3 py-2 ${onShift ? 'bg-blue-50/50' : 'bg-white'}`} data-testid={`rev-emp-${s.id}`}>
                                          <label className="flex min-w-[140px] flex-1 cursor-pointer items-center gap-2 text-sm">
                                            <input type="checkbox" checked={onShift} onChange={() => toggleOnShiftEmployee(s.id)} className="h-4 w-4 rounded text-[#0073BB]" data-testid={`rev-onshift-${s.id}`} />
                                            <span className="text-slate-700">{s.full_name}</span>
                                            <span className="text-xs capitalize text-slate-400">({s.role?.replace('_', ' ')})</span>
                                          </label>
                                          {onShift && (parseInt(revisionForm.us_count) || 0) > 0 && (
                                            <div className="flex items-center gap-1.5">
                                              <span className="text-xs text-slate-500">US indicated:</span>
                                              <Input type="number" min="0" value={revisionForm.us_assignments?.[s.id] ?? 0} onChange={e => setUsAssignment(s.id, e.target.value)} className="h-8 w-16 rounded-sm border-slate-300 font-mono text-sm tabular-nums" data-testid={`rev-us-assign-${s.id}`} />
                                            </div>
                                          )}
                                        </div>
                                      );
                                    })}
                                </div>
                              </div>
                            </div>

                            {/* Step 2: Indication-only employees (only shown when at least one ultrasound is selected) */}
                            {(parseInt(revisionForm.us_count) || 0) > 0 && (
                            <div className="overflow-hidden rounded-sm border border-slate-200">
                              <div className="flex flex-wrap items-center gap-2 border-b border-slate-200 bg-slate-50/60 px-3 py-2">
                                <span className="flex h-5 w-5 items-center justify-center rounded-full bg-slate-400 text-[10px] font-bold text-white">2</span>
                                <span className="text-xs font-semibold text-slate-700">Indication-only employees</span>
                                <span className="text-[11px] text-slate-400">did not work today — get only +$5 per ultrasound they indicated</span>
                              </div>
                              <div className="space-y-2 p-3">
                                  <Input
                                    placeholder="Search employees..."
                                    value={indSearch}
                                    onChange={e => setIndSearch(e.target.value)}
                                    className="h-9 rounded-sm border-slate-300"
                                    data-testid="rev-ind-search"
                                  />
                                  <div className="max-h-44 divide-y divide-slate-100 overflow-y-auto rounded-sm border border-slate-200">
                                    {staffList
                                      .filter(s => (!indSearch || (s.full_name || '').toLowerCase().includes(indSearch.toLowerCase())))
                                      .filter(s => !(revisionForm.on_shift_ids || []).includes(s.id))
                                      .map(s => {
                                        const selected = revisionForm.employee_ids.includes(s.id) && !(revisionForm.on_shift_ids || []).includes(s.id);
                                        return (
                                          <div key={s.id} className={`flex flex-wrap items-center justify-between gap-2 px-3 py-2 ${selected ? 'bg-amber-50/60' : 'bg-white'}`} data-testid={`rev-ind-${s.id}`}>
                                            <label className="flex min-w-[140px] flex-1 cursor-pointer items-center gap-2 text-sm">
                                              <input type="checkbox" checked={selected} onChange={() => toggleIndicationEmployee(s.id)} className="h-4 w-4 rounded text-amber-600" data-testid={`rev-ind-check-${s.id}`} />
                                              <span className="text-slate-700">{s.full_name}</span>
                                              <span className="text-xs capitalize text-slate-400">({s.role?.replace('_', ' ')})</span>
                                            </label>
                                            {selected && (
                                              <div className="flex items-center gap-1.5">
                                                <span className="text-xs text-slate-500">US indicated:</span>
                                                <Input type="number" min="0" value={revisionForm.us_assignments?.[s.id] ?? 0} onChange={e => setUsAssignment(s.id, e.target.value)} className="h-8 w-16 rounded-sm border-slate-300 font-mono text-sm tabular-nums" data-testid={`rev-us-assign-${s.id}`} />
                                              </div>
                                            )}
                                          </div>
                                        );
                                      })}
                                  </div>
                                </div>
                            </div>
                            )}
                          </div>

                          {/* Live bonus preview */}
                          <div className="space-y-3">
                            <SectionLabel>Live Bonus Preview</SectionLabel>
                            <div className="overflow-hidden rounded-sm border border-slate-200 bg-white">
                              <div className="grid grid-cols-2 divide-x divide-slate-100 border-b border-slate-200 sm:grid-cols-4">
                                {[
                                  { l: 'Total Collected', v: formatCurrency(preview.totalCollected) },
                                  { l: 'Bonus Base', v: formatCurrency(preview.base) },
                                  { l: 'Tier', v: preview.tier || 'None' },
                                  { l: 'Base + EKG / emp', v: formatCurrency(preview.perCommon) },
                                ].map(({ l, v }) => (
                                  <div key={l} className="px-3 py-2.5">
                                    <p className="text-[11px] font-medium uppercase tracking-wider text-slate-400">{l}</p>
                                    <p className="font-mono text-sm font-bold tabular-nums text-slate-900">{v}</p>
                                  </div>
                                ))}
                              </div>
                              {preview.employees.length > 0 && (
                                <div className="overflow-x-auto">
                                  <table className="w-full text-sm">
                                    <thead><tr className="bg-slate-50 text-left text-[11px] uppercase tracking-wider text-slate-500"><th className="px-3 py-2 font-semibold">Employee</th><th className="px-3 py-2 font-semibold">On Shift</th><th className="px-3 py-2 font-semibold">US</th><th className="px-3 py-2 text-right font-semibold">Gross</th><th className="px-3 py-2 text-right font-semibold">Net (−7.65%)</th></tr></thead>
                                    <tbody>
                                      {preview.employees.map(e => (
                                        <tr key={e.id} className="border-t border-slate-100 text-slate-700">
                                          <td className="px-3 py-1.5 font-medium text-slate-900">{e.full_name}</td>
                                          <td className="px-3 py-1.5">{e.on_shift ? <span className="font-medium text-emerald-600">Yes</span> : <span className="text-slate-400">No</span>}</td>
                                          <td className="px-3 py-1.5 font-mono tabular-nums">{e.us_indicated > 0 ? e.us_indicated : <span className="text-slate-300">—</span>}</td>
                                          <td className="px-3 py-1.5 text-right font-mono font-semibold tabular-nums text-slate-900">{formatCurrency(e.gross_bonus)}</td>
                                          <td className="px-3 py-1.5 text-right font-mono font-semibold tabular-nums text-emerald-700">{formatCurrency(e.net_bonus)}</td>
                                        </tr>
                                      ))}
                                    </tbody>
                                    <tfoot><tr className="border-t border-slate-200 bg-slate-50 font-bold"><td className="px-3 py-2">Total</td><td></td><td></td><td className="px-3 py-2 text-right font-mono tabular-nums text-slate-900">{formatCurrency(preview.totalGross)}</td><td className="px-3 py-2 text-right font-mono tabular-nums text-emerald-700">{formatCurrency(preview.totalNet)}</td></tr></tfoot>
                                  </table>
                                </div>
                              )}
                            </div>
                          </div>
                          {/* Supporting file upload */}
                          <div className="rounded-md border border-slate-200 bg-white p-4" data-testid="closing-attachment-section">
                            <div className="mb-2 flex items-center gap-2">
                              <Paperclip className="h-4 w-4 text-slate-500" />
                              <h4 className="text-sm font-semibold text-slate-800">Supporting File</h4>
                            </div>
                            <p className="mb-3 text-xs text-slate-500">Attach a document for this closing (PDF, image, Excel, Word or CSV).</p>
                            <input
                              ref={closingFileInputRef}
                              type="file"
                              accept=".pdf,.jpg,.jpeg,.png,.webp,.xlsx,.xls,.csv,.doc,.docx,application/pdf,image/*"
                              className="hidden"
                              onChange={handleClosingFileUpload}
                              data-testid="closing-file-input"
                            />
                            {revisionForm.attachment ? (
                              <div className="flex flex-wrap items-center justify-between gap-2 rounded-sm border border-slate-200 bg-slate-50 px-3 py-2">
                                <div className="flex min-w-0 items-center gap-2">
                                  <FileText className="h-4 w-4 flex-shrink-0 text-[#0073BB]" />
                                  <span className="truncate text-sm font-medium text-slate-800" data-testid="closing-attachment-name">{revisionForm.attachment.filename}</span>
                                </div>
                                <div className="flex items-center gap-2">
                                  <Button type="button" variant="outline" size="sm" className="rounded-sm border-slate-300" onClick={viewClosingAttachment} data-testid="view-closing-attachment-btn"><ExternalLink className="mr-1.5 h-3.5 w-3.5" /> View</Button>
                                  <Button type="button" variant="outline" size="sm" className="rounded-sm border-slate-300" onClick={() => closingFileInputRef.current?.click()} disabled={uploadingClosingFile} data-testid="replace-closing-attachment-btn"><Upload className="mr-1.5 h-3.5 w-3.5" /> Replace</Button>
                                  <Button type="button" variant="outline" size="sm" className="rounded-sm border-red-200 text-red-600 hover:bg-red-50" onClick={deleteClosingAttachment} disabled={uploadingClosingFile} data-testid="delete-closing-attachment-btn"><Trash2 className="h-3.5 w-3.5" /></Button>
                                </div>
                              </div>
                            ) : (
                              <div
                                onClick={() => !uploadingClosingFile && closingFileInputRef.current?.click()}
                                onDragOver={(e) => { e.preventDefault(); setIsDraggingClosingFile(true); }}
                                onDragLeave={(e) => { e.preventDefault(); setIsDraggingClosingFile(false); }}
                                onDrop={handleClosingFileDrop}
                                className={`flex cursor-pointer flex-col items-center justify-center gap-2 rounded-md border-2 border-dashed px-4 py-6 text-center transition-colors ${isDraggingClosingFile ? 'border-[#0073BB] bg-blue-50' : 'border-slate-300 bg-slate-50/50 hover:border-slate-400 hover:bg-slate-50'} ${uploadingClosingFile ? 'pointer-events-none opacity-60' : ''}`}
                                data-testid="closing-file-dropzone"
                              >
                                <Upload className={`h-6 w-6 ${isDraggingClosingFile ? 'text-[#0073BB]' : 'text-slate-400'}`} />
                                {uploadingClosingFile ? (
                                  <p className="text-sm font-medium text-slate-600">Uploading...</p>
                                ) : (
                                  <>
                                    <p className="text-sm font-medium text-slate-700">{isDraggingClosingFile ? 'Drop the file to upload' : 'Drag & drop a file here'}</p>
                                    <p className="text-xs text-slate-400">or <span className="font-medium text-[#0073BB]">click to browse</span></p>
                                  </>
                                )}
                              </div>
                            )}
                          </div>
                        </DialogBody>
                        <DialogFooter className="flex-col items-stretch gap-2 border-t border-slate-200 bg-slate-50/60 px-6 py-4 sm:flex-row sm:items-center sm:justify-end">
                          {(revisionForm.employee_ids || []).length === 0 && (
                            <p className="mr-auto flex items-center gap-1.5 text-xs text-amber-600" data-testid="finalize-hint">
                              <HelpCircle className="h-3.5 w-3.5" /> Add at least one employee (on-shift or indication-only) before finalizing.
                            </p>
                          )}
                          <Button variant="outline" className="rounded-sm border-slate-300" onClick={() => setRevisionDialogOpen(false)} disabled={savingRevision}>Cancel</Button>
                          <Button variant="outline" className="rounded-sm border-slate-300" onClick={() => saveRevision(false)} disabled={savingRevision} data-testid="save-draft-btn">Save Draft</Button>
                          <Button onClick={() => saveRevision(true)} disabled={savingRevision || (revisionForm.employee_ids || []).length === 0} className="rounded-sm bg-[#0073BB] text-white shadow-sm hover:bg-[#005A93] disabled:cursor-not-allowed disabled:opacity-50" data-testid="finalize-btn"><Lock className="mr-1.5 h-4 w-4" /> Finalize</Button>
                        </DialogFooter>
                      </>
                    );
                  })()}
                </DialogContent>
              </Dialog>

              {/* Summary Stats (AWS KPI cards) */}
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
                <div className="rounded-md border border-x border-b border-slate-200 border-t-4 border-t-[#0073BB] bg-white p-4 shadow-sm" data-testid="kpi-total-collected">
                  <div className="mb-2 flex items-center justify-between">
                    <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">Total Collected</p>
                    <DollarSign className="h-4 w-4 text-slate-300" />
                  </div>
                  <p className="font-mono text-3xl font-bold tabular-nums tracking-tight text-slate-900">{formatCurrency(closingReport.total_collected)}</p>
                </div>
                <div className="rounded-md border border-x border-b border-slate-200 border-t-4 border-t-slate-400 bg-white p-4 shadow-sm" data-testid="kpi-total-visits">
                  <div className="mb-2 flex items-center justify-between">
                    <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">Total Visits</p>
                    <Users className="h-4 w-4 text-slate-300" />
                  </div>
                  <p className="font-mono text-3xl font-bold tabular-nums tracking-tight text-slate-900">{closingReport.total_visits}</p>
                </div>
                <div className={`rounded-md border border-x border-b border-slate-200 border-t-4 ${closingReport.bonus > 0 ? 'border-t-[#EC7211]' : 'border-t-slate-300'} bg-white p-4 shadow-sm`} data-testid="kpi-bonus-earned">
                  <div className="mb-2 flex items-center justify-between">
                    <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">Bonus Earned</p>
                    <Award className={`h-4 w-4 ${closingReport.bonus > 0 ? 'text-[#EC7211]' : 'text-slate-300'}`} />
                  </div>
                  <p className={`font-mono text-3xl font-bold tabular-nums tracking-tight ${closingReport.bonus > 0 ? 'text-[#EC7211]' : 'text-slate-900'}`}>{formatCurrency(closingReport.bonus)}</p>
                </div>
                <div className="rounded-md border border-x border-b border-slate-200 border-t-4 border-t-slate-400 bg-white p-4 shadow-sm" data-testid="kpi-visit-types">
                  <p className="mb-2 text-[11px] font-semibold uppercase tracking-wider text-slate-500">Visit Types</p>
                  <div className="flex gap-4">
                    {[
                      { k: 'FU', v: closingReport.visit_type_counts?.FU || 0, c: 'text-[#0073BB]' },
                      { k: 'NP', v: closingReport.visit_type_counts?.NP || 0, c: 'text-emerald-600' },
                      { k: 'NC', v: closingReport.visit_type_counts?.NC || 0, c: 'text-violet-600' },
                    ].map(({ k, v, c }) => (
                      <div key={k} className="flex-1 border-r border-slate-100 pr-3 last:border-r-0 last:pr-0">
                        <p className={`font-mono text-2xl font-bold tabular-nums ${c}`}>{v}</p>
                        <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-400">{k}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Payment & Services Grid */}
              <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
                {/* Payment Breakdown */}
                <div className="flex flex-col overflow-hidden rounded-md border border-slate-200 bg-white shadow-sm">
                  <div className="flex items-center gap-2 border-b border-slate-200 bg-slate-50/60 px-4 py-3">
                    <CreditCard className="h-4 w-4 text-[#0073BB]" />
                    <h3 className="text-sm font-semibold text-slate-800">Payment Breakdown</h3>
                  </div>
                  <div className="flex-1 p-4">
                    <div className="divide-y divide-slate-100">
                      <div className="flex items-center justify-between py-2.5">
                        <div className="flex items-center gap-3">
                          <span className="flex h-7 w-7 items-center justify-center rounded-sm bg-emerald-50 text-emerald-600"><Banknote className="h-4 w-4" /></span>
                          <span className="text-sm text-slate-700">Cash</span>
                        </div>
                        <span className="font-mono text-sm font-semibold tabular-nums text-slate-900">{formatCurrency(closingReport.payment_totals?.cash || 0)}</span>
                      </div>
                      <div className="flex items-center justify-between py-2.5">
                        <div className="flex items-center gap-3">
                          <span className="flex h-7 w-7 items-center justify-center rounded-sm bg-blue-50 text-[#0073BB]"><CreditCard className="h-4 w-4" /></span>
                          <span className="text-sm text-slate-700">Credit/Debit Card</span>
                        </div>
                        <span className="font-mono text-sm font-semibold tabular-nums text-slate-900">{formatCurrency(closingReport.payment_totals?.credit_card || 0)}</span>
                      </div>
                      <div className="flex items-center justify-between py-2.5">
                        <div className="flex items-center gap-3">
                          <span className="flex h-7 w-7 items-center justify-center rounded-sm bg-violet-50 text-violet-600"><Smartphone className="h-4 w-4" /></span>
                          <span className="text-sm text-slate-700">Zelle / CashApp</span>
                        </div>
                        <span className="font-mono text-sm font-semibold tabular-nums text-slate-900">{formatCurrency(closingReport.payment_totals?.zelle_cashapp || 0)}</span>
                      </div>
                      {closingReport.payment_totals?.other > 0 && (
                        <div className="flex items-center justify-between py-2.5">
                          <div className="flex items-center gap-3">
                            <span className="flex h-7 w-7 items-center justify-center rounded-sm bg-slate-100 text-slate-500"><DollarSign className="h-4 w-4" /></span>
                            <span className="text-sm text-slate-700">Other</span>
                          </div>
                          <span className="font-mono text-sm font-semibold tabular-nums text-slate-900">{formatCurrency(closingReport.payment_totals?.other || 0)}</span>
                        </div>
                      )}
                    </div>
                    <div className="mt-2 flex items-center justify-between border-t border-slate-200 pt-3">
                      <span className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">Total Collected</span>
                      <span className="font-mono text-base font-bold tabular-nums text-slate-900">{formatCurrency(closingReport.total_collected)}</span>
                    </div>
                  </div>
                </div>

                {/* Services & Bonus */}
                <div className="flex flex-col overflow-hidden rounded-md border border-slate-200 bg-white shadow-sm">
                  <div className="flex items-center gap-2 border-b border-slate-200 bg-slate-50/60 px-4 py-3">
                    <Activity className="h-4 w-4 text-[#0073BB]" />
                    <h3 className="text-sm font-semibold text-slate-800">Services Performed</h3>
                  </div>
                  <div className="flex-1 p-4">
                    <div className="grid grid-cols-2 gap-3">
                      <div className="rounded-sm border border-slate-200 bg-slate-50/50 p-3">
                        <div className="mb-1 flex items-center gap-2">
                          <Activity className="h-4 w-4 text-rose-500" />
                          <span className="text-xs font-medium text-slate-600">EKG</span>
                        </div>
                        <span className="font-mono text-2xl font-bold tabular-nums text-slate-900">{closingReport.service_counts?.EKG || 0}</span>
                      </div>
                      <div className="rounded-sm border border-slate-200 bg-slate-50/50 p-3">
                        <div className="mb-1 flex items-center gap-2">
                          <Stethoscope className="h-4 w-4 text-indigo-500" />
                          <span className="text-xs font-medium text-slate-600">Ultrasound (US)</span>
                        </div>
                        <span className="font-mono text-2xl font-bold tabular-nums text-slate-900">{closingReport.service_counts?.US || 0}</span>
                      </div>
                    </div>

                    {/* Bonus Calculation sub-panel */}
                    <div className="mt-4 rounded-sm border border-[#FAD6B1] bg-[#FFF2E5] p-3">
                      <p className="mb-2 flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-[#EC7211]"><Award className="h-3.5 w-3.5" /> Bonus Calculation</p>
                      <div className="space-y-1 text-xs">
                        <div className="flex justify-between text-slate-700">
                          <span>Total Collected</span>
                          <span className="font-mono tabular-nums font-medium">{formatCurrency(closingReport.total_collected)}</span>
                        </div>
                        {closingReport.bonus_breakdown && (
                          <>
                            <div className="flex justify-between text-rose-600">
                              <span>− Medications (Other)</span>
                              <span className="font-mono tabular-nums">{formatCurrency(closingReport.bonus_breakdown.medication_other_excluded)}</span>
                            </div>
                            <div className="flex justify-between text-rose-600">
                              <span>− EKG Revenue</span>
                              <span className="font-mono tabular-nums">{formatCurrency(closingReport.bonus_breakdown.ekg_excluded)}</span>
                            </div>
                            <div className="flex justify-between text-rose-600">
                              <span>− Ultrasound Revenue</span>
                              <span className="font-mono tabular-nums">{formatCurrency(closingReport.bonus_breakdown.ultrasound_excluded)}</span>
                            </div>
                            <div className="mt-1 flex justify-between border-t border-[#FAD6B1] pt-1 font-semibold text-slate-800">
                              <span>Bonus Base Amount</span>
                              <span className="font-mono tabular-nums">{formatCurrency(closingReport.bonus_breakdown.base_amount)}</span>
                            </div>
                          </>
                        )}
                      </div>
                      <p className="mt-2 border-t border-[#FAD6B1] pt-2 text-xs text-slate-600">
                        <span className="font-semibold">Tier:</span> {closingReport.bonus_breakdown.bonus_tier || 'Below $2,000'}
                        <span className="ml-1 text-slate-500">($50 at $2K | $75 at $2.5K | $100 at $3K | $125 at $3.5K)</span>
                      </p>
                      {closingReport.bonus_breakdown && (closingReport.bonus_breakdown.ekg_count > 0 || closingReport.bonus_breakdown.us_count > 0) && (
                        <div className="mt-2 space-y-1 text-xs text-emerald-700">
                          {closingReport.bonus_breakdown.ekg_count > 0 && (
                            <div className="flex justify-between">
                              <span>+ EKG Bonus ({closingReport.bonus_breakdown.ekg_count} × $5)</span>
                              <span className="font-mono tabular-nums font-medium">{formatCurrency(closingReport.bonus_breakdown.ekg_bonus)}</span>
                            </div>
                          )}
                          {closingReport.bonus_breakdown.us_count > 0 && (
                            <div className="flex justify-between">
                              <span>+ Ultrasound Bonus ({closingReport.bonus_breakdown.us_count} × $5)</span>
                              <span className="font-mono tabular-nums font-medium">{formatCurrency(closingReport.bonus_breakdown.us_bonus)}</span>
                            </div>
                          )}
                        </div>
                      )}
                      {closingReport.bonus > 0 && (
                        <div className="mt-2 flex items-center justify-between border-t border-[#FAD6B1] pt-2">
                          <span className="text-sm font-bold text-[#EC7211]">Total Bonus</span>
                          <span className="font-mono text-sm font-bold tabular-nums text-[#EC7211]">{formatCurrency(closingReport.bonus)}</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Patient Details Table */}
              <div className="overflow-hidden rounded-md border border-slate-200 bg-white shadow-sm">
                <div className="flex items-center gap-2 border-b border-slate-200 bg-slate-50/60 px-4 py-3">
                  <FileText className="h-4 w-4 text-[#0073BB]" />
                  <h3 className="text-sm font-semibold text-slate-800">Patient Visit Details</h3>
                  {closingReport.patient_visits?.length > 0 && (
                    <span className="ml-auto rounded-sm bg-slate-100 px-2 py-0.5 font-mono text-xs font-medium text-slate-600">{closingReport.patient_visits.length} rows</span>
                  )}
                </div>
                {closingReport.patient_visits?.length > 0 ? (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-slate-200 bg-[#F8F9FA] text-left text-[11px] uppercase tracking-wider text-slate-600">
                          <th className="px-4 py-2.5 font-semibold">Patient</th>
                          <th className="px-4 py-2.5 font-semibold">Bill ID</th>
                          <th className="px-4 py-2.5 font-semibold">Type</th>
                          <th className="px-4 py-2.5 font-semibold">Doctor</th>
                          <th className="px-4 py-2.5 text-center font-semibold">Services</th>
                          <th className="px-4 py-2.5 text-center font-semibold">EKG</th>
                          <th className="px-4 py-2.5 text-center font-semibold">US</th>
                          <th className="px-4 py-2.5 text-right font-semibold">Amount</th>
                          <th className="px-4 py-2.5 font-semibold">Payment</th>
                        </tr>
                      </thead>
                      <tbody>
                        {closingReport.patient_visits.map((visit, idx) => (
                          <tr key={idx} className="border-b border-slate-100 hover:bg-slate-50/80">
                            <td className="px-4 py-2.5 font-medium text-slate-900">{visit.patient_name}</td>
                            <td className="px-4 py-2.5">
                              <div className="flex flex-col gap-1">
                                {visit.bill_ids?.length > 0 ? (
                                  visit.bill_ids.map((billId, bidx) => (
                                    <span key={bidx} className="w-fit rounded-sm bg-blue-50 px-2 py-0.5 font-mono text-xs font-bold uppercase text-[#0073BB]">
                                      {billId}
                                    </span>
                                  ))
                                ) : (
                                  <span className="text-xs text-slate-400">-</span>
                                )}
                              </div>
                            </td>
                            <td className="px-4 py-2.5">
                              <span className="rounded-sm bg-slate-100 px-2 py-1 text-xs font-medium text-slate-700">{visit.visit_type}</span>
                            </td>
                            <td className="px-4 py-2.5 text-slate-600">{visit.doctor_name || '-'}</td>
                            <td className="px-4 py-2.5 text-center font-mono tabular-nums text-slate-600">{visit.services?.length || 0}</td>
                            <td className="px-4 py-2.5 text-center">
                              {visit.has_ekg ? <span className="font-semibold text-slate-900">Yes</span> : <span className="text-slate-300">-</span>}
                            </td>
                            <td className="px-4 py-2.5 text-center">
                              {visit.has_us ? <span className="font-semibold text-slate-900">Yes</span> : <span className="text-slate-300">-</span>}
                            </td>
                            <td className="px-4 py-2.5 text-right font-mono font-semibold tabular-nums text-slate-900">{formatCurrency(visit.amount_paid)}</td>
                            <td className="px-4 py-2.5 text-slate-600">{visit.payment_method || '-'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="py-12 text-center text-slate-500">
                    <Calendar className="mx-auto mb-3 h-12 w-12 text-slate-300" />
                    <p className="text-sm">No visits recorded for this date</p>
                  </div>
                )}
              </div>

              {/* Footer Summary (AWS dark bar) */}
              <div className="rounded-md border border-slate-700 bg-slate-900 p-4 text-white">
                <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                  <div className="flex flex-wrap items-center gap-6">
                    <div>
                      <span className="text-[11px] font-semibold uppercase tracking-wider text-slate-400">Total Visits</span>
                      <span className="ml-2 font-mono text-lg font-bold tabular-nums">{closingReport.total_visits}</span>
                    </div>
                    <div className="flex items-center gap-4 text-sm">
                      <span><span className="text-slate-400">FU:</span> <span className="font-mono tabular-nums text-white">{closingReport.visit_type_counts?.FU || 0}</span></span>
                      <span><span className="text-slate-400">NP:</span> <span className="font-mono tabular-nums text-white">{closingReport.visit_type_counts?.NP || 0}</span></span>
                      <span><span className="text-slate-400">NC:</span> <span className="font-mono tabular-nums text-white">{closingReport.visit_type_counts?.NC || 0}</span></span>
                    </div>
                  </div>
                  <div className="flex flex-wrap items-center gap-6">
                    <div>
                      <span className="text-[11px] font-semibold uppercase tracking-wider text-slate-400">Total Collected</span>
                      <span className="ml-2 font-mono text-xl font-bold tabular-nums">{formatCurrency(closingReport.total_collected)}</span>
                    </div>
                    {closingReport.bonus > 0 && (
                      <div className="rounded-sm bg-[#EC7211] px-4 py-2 text-white">
                        <span className="text-[11px] font-semibold uppercase tracking-wider">Bonus</span>
                        <span className="ml-2 font-mono text-lg font-bold tabular-nums">{formatCurrency(closingReport.bonus)}</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <Card className="border-slate-200">
              <CardContent className="py-12 text-center text-slate-500">
                <Calendar className="mx-auto mb-4 h-16 w-16 text-slate-300" />
                <p>Select a date to view the closing report</p>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* ==================== REVENUE TAB ==================== */}
        <TabsContent value="revenue" className="space-y-6">
          {/* Filters */}
          <Card className="border-gray-200">
            <CardContent className="py-4">
              <div className="flex flex-wrap items-end gap-4">
                <div>
                  <Label className="text-xs uppercase tracking-wide text-gray-500">Start Date</Label>
                  <Input
                    type="date"
                    value={dateRange.start}
                    onChange={(e) => setDateRange({ ...dateRange, start: e.target.value })}
                    className="w-40 mt-1"
                    data-testid="report-start-date"
                  />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wide text-gray-500">End Date</Label>
                  <Input
                    type="date"
                    value={dateRange.end}
                    onChange={(e) => setDateRange({ ...dateRange, end: e.target.value })}
                    className="w-40 mt-1"
                    data-testid="report-end-date"
                  />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wide text-gray-500">Location</Label>
                  <Select value={selectedLocation} onValueChange={setSelectedLocation}>
                    <SelectTrigger className="w-48 mt-1" data-testid="report-location-filter">
                      <SelectValue placeholder={hasAssignedLocations ? "Select Location" : "All Locations"} />
                    </SelectTrigger>
                    <SelectContent>
                      {!hasAssignedLocations && (
                        <SelectItem value="all">All Locations</SelectItem>
                      )}
                      {locations.map((loc) => (
                        <SelectItem key={loc.id} value={loc.id}>{loc.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <Button 
                  onClick={fetchReports} 
                  className="bg-blue-700 hover:bg-blue-800"
                  data-testid="refresh-reports-btn"
                >
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Refresh
                </Button>
              </div>
            </CardContent>
          </Card>

          {loading ? (
            <div className="flex items-center justify-center h-64">
              <RefreshCw className="w-8 h-8 animate-spin text-blue-600" />
            </div>
          ) : (
            <>
              {/* Summary Stats */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <StatCard 
                  label="Total Revenue" 
                  value={formatCurrency(revenueReport?.total_revenue)}
                  icon={TrendingUp}
                  highlight={true}
                />
                <StatCard 
                  label="Total Bills" 
                  value={revenueReport?.total_bills || 0}
                  icon={FileText}
                />
                <StatCard 
                  label="Avg. Transaction" 
                  value={formatCurrency(revenueReport?.average_transaction)}
                  icon={BarChart3}
                  accent={true}
                />
              </div>

              {/* Charts */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <Card className="border-gray-200">
                  <CardHeader className="pb-3 border-b border-gray-100">
                    <CardTitle className="text-sm font-medium text-gray-700">Revenue by Location</CardTitle>
                  </CardHeader>
                  <CardContent className="h-[300px] pt-4">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={getLocationChartData()}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                        <XAxis dataKey="name" tick={{ fontSize: 12 }} stroke="#6b7280" />
                        <YAxis tick={{ fontSize: 12 }} stroke="#6b7280" />
                        <Tooltip 
                          formatter={(value) => formatCurrency(value)}
                          contentStyle={{ borderRadius: '8px', border: '1px solid #e5e7eb' }}
                        />
                        <Bar dataKey="revenue" fill="#0f766e" radius={[4, 4, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>

                <Card className="border-gray-200">
                  <CardHeader className="pb-3 border-b border-gray-100">
                    <CardTitle className="text-sm font-medium text-gray-700">Payment Methods</CardTitle>
                  </CardHeader>
                  <CardContent className="h-[300px] pt-4">
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={getPaymentMethodChartData()}
                          cx="50%"
                          cy="50%"
                          labelLine={false}
                          label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                          outerRadius={100}
                          fill="#8884d8"
                          dataKey="value"
                        >
                          {getPaymentMethodChartData().map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                          ))}
                        </Pie>
                        <Tooltip 
                          formatter={(value) => formatCurrency(value)}
                          contentStyle={{ borderRadius: '8px', border: '1px solid #e5e7eb' }}
                        />
                        <Legend />
                      </PieChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>
              </div>
            </>
          )}
        </TabsContent>

        {/* ==================== INVENTORY TAB ==================== */}
        {canSeeAllReports && (
          <TabsContent value="inventory" className="space-y-6" data-testid="medication-usage-content">
          <Card className="border-gray-200">
            <CardContent className="p-6">
              {/* Date Range Filters */}
              <div className="flex flex-wrap items-center gap-4 mb-6">
                <div className="flex items-center gap-2">
                  <Calendar className="w-4 h-4 text-slate-500" />
                  <span className="text-sm font-medium text-slate-700">Date Range:</span>
                </div>
                
                <div className="flex gap-2">
                  {[
                    { key: 'last7', label: 'Last 7 Days' },
                    { key: 'last30', label: 'Last 30 Days' },
                    { key: 'lastMonth', label: 'Last Month' },
                    { key: 'thisMonth', label: 'This Month' },
                    { key: 'last90', label: 'Last 90 Days' }
                  ].map(preset => (
                    <Button
                      key={preset.key}
                      variant="outline"
                      size="sm"
                      onClick={() => setQuickUsageDateRange(preset.key)}
                      className="text-xs"
                      data-testid={`usage-preset-${preset.key}`}
                    >
                      {preset.label}
                    </Button>
                  ))}
                </div>
                
                <div className="flex items-center gap-2 ml-4">
                  <Input
                    type="date"
                    value={usageDateRange.start}
                    onChange={(e) => {
                      const newRange = { ...usageDateRange, start: e.target.value };
                      setUsageDateRange(newRange);
                      setUsagePage(1);
                      fetchUsageReport(newRange, usageSortBy, usageSortOrder, 1);
                    }}
                    className="w-36 h-8 text-sm"
                    data-testid="usage-start-date"
                  />
                  <span className="text-slate-400">to</span>
                  <Input
                    type="date"
                    value={usageDateRange.end}
                    onChange={(e) => {
                      const newRange = { ...usageDateRange, end: e.target.value };
                      setUsageDateRange(newRange);
                      setUsagePage(1);
                      fetchUsageReport(newRange, usageSortBy, usageSortOrder, 1);
                    }}
                    className="w-36 h-8 text-sm"
                    data-testid="usage-end-date"
                  />
                </div>
                
                {usageLoading && (
                  <RefreshCw className="w-4 h-4 animate-spin text-blue-500" />
                )}
              </div>
              
              {/* Header with title, pagination info, and export button */}
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-slate-800">
                  Medication Usage
                  {usageReport && (
                    <span className="text-sm font-normal text-slate-500 ml-2">
                      ({new Date(usageDateRange.start).toLocaleDateString()} - {new Date(usageDateRange.end).toLocaleDateString()})
                      {usageReport.pagination && (
                        <span className="ml-2">• {usageReport.pagination.total_count} items</span>
                      )}
                    </span>
                  )}
                </h3>
                <div className="flex items-center gap-3">
                  <p className="text-sm text-slate-500">Click column headers to sort</p>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleExportUsage}
                    disabled={exportingUsage || !usageReport}
                    className="flex items-center gap-2 border-blue-200 text-blue-700 hover:bg-blue-50"
                    data-testid="export-usage-btn"
                  >
                    {exportingUsage ? (
                      <RefreshCw className="w-4 h-4 animate-spin" />
                    ) : (
                      <Download className="w-4 h-4" />
                    )}
                    Export Excel
                  </Button>
                </div>
              </div>
              
              {/* Usage Table */}
              {usageLoading ? (
                <div className="flex items-center justify-center py-12">
                  <RefreshCw className="w-8 h-8 animate-spin text-blue-500" />
                </div>
              ) : usageReport ? (
                usageReport.medications.length > 0 ? (
                  <>
                  <div className="border rounded-lg overflow-hidden">
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-slate-50">
                          <TableHead className="w-8"></TableHead>
                          <TableHead className="font-semibold">#</TableHead>
                          <TableHead 
                            className="font-semibold cursor-pointer hover:bg-slate-100"
                            onClick={() => handleUsageSort('medication_name')}
                          >
                            <div className="flex items-center gap-1">
                              Medication Name
                              {usageSortBy === 'medication_name' && (
                                usageSortOrder === 'desc' ? <ArrowDown className="w-3 h-3" /> : <ArrowUp className="w-3 h-3" />
                              )}
                            </div>
                          </TableHead>
                          <TableHead className="font-semibold">SKU</TableHead>
                          <TableHead 
                            className="font-semibold text-center cursor-pointer hover:bg-slate-100"
                            onClick={() => handleUsageSort('total_quantity')}
                          >
                            <div className="flex items-center justify-center gap-1">
                              Qty Dispensed
                              {usageSortBy === 'total_quantity' && (
                                usageSortOrder === 'desc' ? <ArrowDown className="w-3 h-3" /> : <ArrowUp className="w-3 h-3" />
                              )}
                            </div>
                          </TableHead>
                          <TableHead 
                            className="font-semibold text-center cursor-pointer hover:bg-slate-100"
                            onClick={() => handleUsageSort('times_dispensed')}
                          >
                            <div className="flex items-center justify-center gap-1">
                              Times Used
                              {usageSortBy === 'times_dispensed' && (
                                usageSortOrder === 'desc' ? <ArrowDown className="w-3 h-3" /> : <ArrowUp className="w-3 h-3" />
                              )}
                            </div>
                          </TableHead>
                          <TableHead 
                            className="font-semibold text-right cursor-pointer hover:bg-slate-100"
                            onClick={() => handleUsageSort('total_revenue')}
                          >
                            <div className="flex items-center justify-end gap-1">
                              Revenue
                              {usageSortBy === 'total_revenue' && (
                                usageSortOrder === 'desc' ? <ArrowDown className="w-3 h-3" /> : <ArrowUp className="w-3 h-3" />
                              )}
                            </div>
                          </TableHead>
                          <TableHead 
                            className="font-semibold text-center cursor-pointer hover:bg-slate-100"
                            onClick={() => handleUsageSort('current_stock')}
                          >
                            <div className="flex items-center justify-center gap-1">
                              Current Stock
                              {usageSortBy === 'current_stock' && (
                                usageSortOrder === 'desc' ? <ArrowDown className="w-3 h-3" /> : <ArrowUp className="w-3 h-3" />
                              )}
                            </div>
                          </TableHead>
                          <TableHead className="font-semibold">Location</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {usageReport.medications.map((med, index) => (
                          <React.Fragment key={med.inventory_id || index}>
                            <TableRow 
                              className={`hover:bg-slate-50 cursor-pointer ${expandedMedications[med.inventory_id] ? 'bg-blue-50' : ''} ${med.total_quantity === 0 ? 'opacity-60' : ''}`}
                              onClick={() => med.times_dispensed > 0 ? toggleMedicationExpand(med.inventory_id) : null}
                              data-testid={`usage-row-${med.inventory_id}`}
                            >
                              <TableCell className="w-8">
                                {med.times_dispensed > 0 && (
                                  loadingBillHistory[med.inventory_id] ? (
                                    <RefreshCw className="w-4 h-4 animate-spin text-blue-500" />
                                  ) : expandedMedications[med.inventory_id] ? (
                                    <ChevronDown className="w-4 h-4 text-blue-500" />
                                  ) : (
                                    <ChevronRight className="w-4 h-4 text-slate-400" />
                                  )
                                )}
                              </TableCell>
                              <TableCell className="font-medium text-slate-500">{((usagePage - 1) * usagePageSize) + index + 1}</TableCell>
                              <TableCell>
                                <div className="flex items-center gap-2">
                                  <Pill className="w-4 h-4 text-blue-500" />
                                  <span className="font-medium">{med.medication_name}</span>
                                </div>
                              </TableCell>
                              <TableCell className="text-slate-500 text-sm">{med.sku || '-'}</TableCell>
                              <TableCell className="text-center">
                                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-sm font-semibold ${
                                  med.total_quantity === 0 ? 'bg-slate-100 text-slate-500' : 'bg-blue-100 text-blue-800'
                                }`}>
                                  {med.total_quantity}
                                </span>
                              </TableCell>
                              <TableCell className="text-center text-slate-600">{med.times_dispensed}</TableCell>
                              <TableCell className="text-right font-semibold text-green-700">${med.total_revenue.toFixed(2)}</TableCell>
                              <TableCell className="text-center">
                                <span className={`inline-flex items-center px-2 py-0.5 rounded text-sm font-medium ${
                                  med.current_stock <= 10 
                                    ? 'bg-red-100 text-red-800' 
                                    : med.current_stock <= 25 
                                    ? 'bg-amber-100 text-amber-800'
                                    : 'bg-green-100 text-green-800'
                                }`}>
                                  {med.current_stock}
                                </span>
                              </TableCell>
                              <TableCell className="text-sm text-slate-600">{med.location_name || '—'}</TableCell>
                            </TableRow>
                            
                            {/* Expanded Bill History */}
                            {expandedMedications[med.inventory_id] && (
                              <TableRow>
                                <TableCell colSpan={9} className="bg-slate-50 p-0">
                                  <div className="p-4 border-l-4 border-blue-400">
                                    {loadingBillHistory[med.inventory_id] ? (
                                      <div className="flex items-center gap-2 text-slate-500">
                                        <RefreshCw className="w-4 h-4 animate-spin" />
                                        Loading bill history...
                                      </div>
                                    ) : medicationBillHistory[med.inventory_id]?.length > 0 ? (
                                      <>
                                        <h4 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
                                          <History className="w-4 h-4" />
                                          Bill History ({medicationBillHistory[med.inventory_id].length} of {billHistoryMeta[med.inventory_id]?.total_count || medicationBillHistory[med.inventory_id].length} bills)
                                        </h4>
                                        <div className="space-y-2">
                                          {medicationBillHistory[med.inventory_id].map((bill, billIndex) => (
                                            <div 
                                              key={billIndex}
                                              className="flex items-center justify-between bg-white rounded-lg p-3 border border-slate-200 hover:border-blue-300 transition-colors"
                                            >
                                              <div className="flex items-center gap-4">
                                                <button
                                                  onClick={(e) => {
                                                    e.stopPropagation();
                                                    navigateToBill(bill.bill_id);
                                                  }}
                                                  className="text-blue-600 hover:text-blue-800 font-medium flex items-center gap-1 hover:underline"
                                                  data-testid={`bill-link-${bill.bill_id}`}
                                                >
                                                  <FileText className="w-4 h-4" />
                                                  #{bill.bill_id?.slice(-8).toUpperCase()}
                                                  <ExternalLink className="w-3 h-3" />
                                                </button>
                                                <span className="text-slate-600">{bill.patient_name}</span>
                                                <span className="text-slate-400 text-sm">
                                                  {new Date(bill.date).toLocaleDateString()} {new Date(bill.date).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
                                                </span>
                                              </div>
                                              <div className="flex items-center gap-4">
                                                <span className="text-sm text-slate-600">
                                                  Qty: <span className="font-semibold">{bill.quantity}</span>
                                                </span>
                                                <span className="text-sm font-semibold text-green-700">
                                                  ${bill.total.toFixed(2)}
                                                </span>
                                              </div>
                                            </div>
                                          ))}
                                        </div>
                                        {billHistoryMeta[med.inventory_id]?.has_more && (
                                          <button
                                            onClick={(e) => { e.stopPropagation(); loadMoreBillHistory(med.inventory_id); }}
                                            disabled={loadingBillHistory[med.inventory_id]}
                                            className="mt-3 w-full py-2 text-sm font-medium text-blue-600 bg-blue-50 hover:bg-blue-100 rounded-lg border border-blue-200 transition-colors disabled:opacity-50"
                                            data-testid={`load-more-history-${med.inventory_id}`}
                                          >
                                            {loadingBillHistory[med.inventory_id] ? 'Loading...' : `Load More (${billHistoryMeta[med.inventory_id].total_count - medicationBillHistory[med.inventory_id].length} remaining)`}
                                          </button>
                                        )}
                                      </>
                                    ) : (
                                      <p className="text-slate-500 text-sm">No bill history found for this medication.</p>
                                    )}
                                  </div>
                                </TableCell>
                              </TableRow>
                            )}
                          </React.Fragment>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                  
                  {/* Pagination */}
                  {usageReport.pagination && usageReport.pagination.total_pages > 1 && (
                    <div className="flex items-center justify-between mt-4 px-2">
                      <p className="text-sm text-slate-500">
                        Page {usageReport.pagination.page} of {usageReport.pagination.total_pages} ({usageReport.pagination.total_count} total)
                      </p>
                      <div className="flex items-center gap-2">
                        <Button variant="outline" size="sm" onClick={() => handleUsagePageChange(1)} disabled={usagePage === 1}>
                          <ChevronsLeft className="w-4 h-4" />
                        </Button>
                        <Button variant="outline" size="sm" onClick={() => handleUsagePageChange(usagePage - 1)} disabled={usagePage === 1}>
                          <ChevronLeft className="w-4 h-4" />
                        </Button>
                        
                        {(() => {
                          const totalPages = usageReport.pagination.total_pages;
                          const cp = usagePage;
                          let pages = [];
                          if (totalPages <= 5) {
                            pages = Array.from({length: totalPages}, (_, i) => i + 1);
                          } else if (cp <= 3) {
                            pages = [1, 2, 3, 4, '...', totalPages];
                          } else if (cp >= totalPages - 2) {
                            pages = [1, '...', totalPages - 3, totalPages - 2, totalPages - 1, totalPages];
                          } else {
                            pages = [1, '...', cp - 1, cp, cp + 1, '...', totalPages];
                          }
                          return pages.map((p, i) => (
                            p === '...' ? (
                              <span key={`ellipsis-${i}`} className="px-2 text-slate-400">...</span>
                            ) : (
                              <Button
                                key={p}
                                variant={p === cp ? 'default' : 'outline'}
                                size="sm"
                                onClick={() => handleUsagePageChange(p)}
                                className={p === cp ? 'bg-blue-600 text-white' : ''}
                              >
                                {p}
                              </Button>
                            )
                          ));
                        })()}
                        
                        <Button variant="outline" size="sm" onClick={() => handleUsagePageChange(usagePage + 1)} disabled={usagePage === usageReport.pagination.total_pages}>
                          <ChevronRight className="w-4 h-4" />
                        </Button>
                        <Button variant="outline" size="sm" onClick={() => handleUsagePageChange(usageReport.pagination.total_pages)} disabled={usagePage === usageReport.pagination.total_pages}>
                          <ChevronsRight className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  )}
                  </>
                ) : (
                  <div className="text-center py-12 text-slate-500">
                    <Pill className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                    <p className="text-lg font-medium">No medication data found</p>
                    <p className="text-sm">No medications found for this period and filters.</p>
                  </div>
                )
              ) : (
                <div className="text-center py-12 text-slate-500">
                  <BarChart3 className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                  <p className="text-lg font-medium">Loading...</p>
                  <p className="text-sm">Fetching medication usage data.</p>
                </div>
              )}
            </CardContent>
          </Card>

          </TabsContent>
        )}

        {/* ==================== DOCTORS TAB ==================== */}
        {canSeeAllReports && (
          <TabsContent value="doctors" className="space-y-6">
            <Card className="border-gray-200">
              <CardContent className="py-4">
                <div className="flex flex-wrap items-end gap-4">
                  <div>
                    <Label className="text-xs uppercase tracking-wide text-gray-500">Start Date</Label>
                    <Input
                      type="date"
                      value={dateRange.start}
                      onChange={(e) => setDateRange({ ...dateRange, start: e.target.value })}
                      className="w-40 mt-1"
                    />
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-wide text-gray-500">End Date</Label>
                    <Input
                      type="date"
                      value={dateRange.end}
                      onChange={(e) => setDateRange({ ...dateRange, end: e.target.value })}
                      className="w-40 mt-1"
                    />
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-wide text-gray-500">Location</Label>
                    <Select value={selectedLocation} onValueChange={setSelectedLocation}>
                      <SelectTrigger className="w-48 mt-1">
                        <SelectValue placeholder={hasAssignedLocations ? "Select Location" : "All Locations"} />
                      </SelectTrigger>
                      <SelectContent>
                        {!hasAssignedLocations && (
                          <SelectItem value="all">All Locations</SelectItem>
                        )}
                        {locations.map((loc) => (
                          <SelectItem key={loc.id} value={loc.id}>{loc.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <Button onClick={fetchReports} className="bg-blue-700 hover:bg-blue-800">
                    <RefreshCw className="w-4 h-4 mr-2" />
                    Refresh
                  </Button>
                </div>
              </CardContent>
            </Card>

            {loading ? (
              <div className="flex items-center justify-center h-64">
                <RefreshCw className="w-8 h-8 animate-spin text-blue-600" />
              </div>
            ) : doctorReport && (
              <Card className="border-gray-200">
                <CardHeader className="pb-3 border-b border-gray-100">
                  <CardTitle className="text-sm font-medium text-blue-700">
                    Doctor Productivity - {doctorReport.period}
                  </CardTitle>
                </CardHeader>
                <CardContent className="pt-4">
                  <Table>
                    <TableHeader>
                      <TableRow className="border-b border-gray-200">
                        <TableHead className="text-xs uppercase tracking-wide text-gray-500 font-medium">Doctor</TableHead>
                        <TableHead className="text-xs uppercase tracking-wide text-gray-500 font-medium">Total Visits</TableHead>
                        <TableHead className="text-xs uppercase tracking-wide text-gray-500 font-medium">Services Performed</TableHead>
                        <TableHead className="text-xs uppercase tracking-wide text-gray-500 font-medium text-right">Revenue Generated</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {doctorReport.doctors?.map((doc, idx) => (
                        <TableRow key={idx} className="border-b border-gray-100">
                          <TableCell className="font-medium text-gray-900">{doc.doctor_name}</TableCell>
                          <TableCell className="text-gray-600">{doc.total_visits}</TableCell>
                          <TableCell className="text-gray-600">{doc.total_services}</TableCell>
                          <TableCell className="text-right font-semibold text-gray-900">
                            {formatCurrency(doc.total_revenue)}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            )}
          </TabsContent>
        )}

        {/* ==================== OUTSTANDING TAB ==================== */}
        {canSeeAllReports && (
          <>
          <TabsContent value="outstanding" className="space-y-6">
            <Card className="border-gray-200">
              <CardContent className="py-4">
                <div className="flex flex-wrap items-end gap-4">
                  <div>
                    <Label className="text-xs uppercase tracking-wide text-gray-500">Location</Label>
                    <Select value={selectedLocation} onValueChange={setSelectedLocation}>
                      <SelectTrigger className="w-48 mt-1">
                        <SelectValue placeholder={hasAssignedLocations ? "Select Location" : "All Locations"} />
                      </SelectTrigger>
                      <SelectContent>
                        {!hasAssignedLocations && (
                          <SelectItem value="all">All Locations</SelectItem>
                        )}
                        {locations.map((loc) => (
                          <SelectItem key={loc.id} value={loc.id}>{loc.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <Button onClick={fetchReports} className="bg-blue-700 hover:bg-blue-800">
                    <RefreshCw className="w-4 h-4 mr-2" />
                    Refresh
                  </Button>
                </div>
              </CardContent>
            </Card>

            {loading ? (
              <div className="flex items-center justify-center h-64">
                <RefreshCw className="w-8 h-8 animate-spin text-blue-600" />
              </div>
            ) : outstandingReport && (
              <>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <StatCard 
                    label="Total Outstanding" 
                    value={formatCurrency(outstandingReport.total_outstanding)}
                    icon={AlertTriangle}
                    highlight={true}
                  />
                  <StatCard 
                    label="Outstanding Bills" 
                    value={outstandingReport.total_bills}
                    icon={FileText}
                  />
                </div>

                {outstandingReport.bills?.length > 0 && (
                  <Card className="border-gray-200">
                    <CardHeader className="pb-3 border-b border-gray-100">
                      <CardTitle className="text-sm font-medium text-gray-700">Outstanding Bills</CardTitle>
                    </CardHeader>
                    <CardContent className="pt-4">
                      <Table>
                        <TableHeader>
                          <TableRow className="border-b border-gray-200">
                            <TableHead className="text-xs uppercase tracking-wide text-gray-500 font-medium">Patient</TableHead>
                            <TableHead className="text-xs uppercase tracking-wide text-gray-500 font-medium">Location</TableHead>
                            <TableHead className="text-xs uppercase tracking-wide text-gray-500 font-medium text-right">Total</TableHead>
                            <TableHead className="text-xs uppercase tracking-wide text-gray-500 font-medium text-right">Paid</TableHead>
                            <TableHead className="text-xs uppercase tracking-wide text-gray-500 font-medium text-right">Balance</TableHead>
                            <TableHead className="text-xs uppercase tracking-wide text-gray-500 font-medium">Date</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {outstandingReport.bills.slice(0, 20).map((bill, idx) => (
                            <TableRow key={idx} className="border-b border-gray-100">
                              <TableCell className="font-medium text-gray-900">{bill.patient_name}</TableCell>
                              <TableCell className="text-gray-600">{bill.location_name}</TableCell>
                              <TableCell className="text-right text-gray-600">{formatCurrency(bill.total)}</TableCell>
                              <TableCell className="text-right text-gray-600">{formatCurrency(bill.amount_paid)}</TableCell>
                              <TableCell className="text-right font-semibold text-gray-900">{formatCurrency(bill.balance_due)}</TableCell>
                              <TableCell className="text-gray-600">{new Date(bill.created_at).toLocaleDateString()}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </CardContent>
                  </Card>
                )}
              </>
            )}
          </TabsContent>

          <TabsContent value="bonus-jotform" className="space-y-6" data-testid="bonus-jotform-content">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      <Award className="w-5 h-5 text-amber-600" />
                      Bonus Summary (JotForm)
                    </CardTitle>
                    {jotformPeriod.start && (
                      <p className="text-sm text-slate-500 mt-1">
                        Bi-weekly Period: {jotformPeriod.start} to {jotformPeriod.end}
                      </p>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="flex rounded-lg border border-slate-200 overflow-hidden">
                      <button
                        onClick={() => { setJotformPeriodType('previous'); fetchBonusJotform('previous'); }}
                        className={`px-3 py-1.5 text-sm font-medium transition-colors ${
                          jotformPeriodType === 'previous' 
                            ? 'bg-blue-600 text-white' 
                            : 'bg-white text-slate-600 hover:bg-slate-50'
                        }`}
                        data-testid="jotform-period-previous"
                      >
                        Previous
                      </button>
                      <button
                        onClick={() => { setJotformPeriodType('current'); fetchBonusJotform('current'); }}
                        className={`px-3 py-1.5 text-sm font-medium transition-colors ${
                          jotformPeriodType === 'current' 
                            ? 'bg-blue-600 text-white' 
                            : 'bg-white text-slate-600 hover:bg-slate-50'
                        }`}
                        data-testid="jotform-period-current"
                      >
                        Current
                      </button>
                    </div>
                    <Button variant="outline" size="sm" onClick={() => fetchBonusJotform()} disabled={jotformLoading}>
                      <RefreshCw className={`w-4 h-4 mr-1 ${jotformLoading ? 'animate-spin' : ''}`} />
                      Refresh
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {jotformLoading ? (
                  <div className="text-center py-8 text-slate-500">Loading bonus data...</div>
                ) : !jotformData || jotformData.length === 0 ? (
                  <div className="text-center py-8 text-slate-500">No bonus data available for this period.</div>
                ) : (
                  <div className="space-y-6">
                    {/* Summary Cards */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                        <p className="text-xs text-amber-600 uppercase font-medium">Total Employees</p>
                        <p className="text-2xl font-bold text-amber-800">{jotformData.length}</p>
                      </div>
                      <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                        <p className="text-xs text-green-600 uppercase font-medium">Total Bonus</p>
                        <p className="text-2xl font-bold text-green-800">
                          ${jotformData.reduce((sum, e) => sum + (e.totalBonus || 0), 0).toFixed(2)}
                        </p>
                      </div>
                      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                        <p className="text-xs text-blue-600 uppercase font-medium">Doctors</p>
                        <p className="text-2xl font-bold text-blue-800">
                          {jotformData.filter(e => e.role === 'Doctor').length}
                        </p>
                      </div>
                      <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                        <p className="text-xs text-purple-600 uppercase font-medium">Staff</p>
                        <p className="text-2xl font-bold text-purple-800">
                          {jotformData.filter(e => e.role !== 'Doctor').length}
                        </p>
                      </div>
                    </div>

                    {/* Employee Bonus Table */}
                    <div className="border border-slate-200 rounded-lg overflow-hidden">
                      <Table>
                        <TableHeader>
                          <TableRow className="bg-slate-50">
                            <TableHead className="font-semibold">Name</TableHead>
                            <TableHead className="font-semibold">Role</TableHead>
                            <TableHead className="font-semibold">Location</TableHead>
                            <TableHead className="font-semibold text-right">Total Bonus</TableHead>
                            <TableHead className="font-semibold text-center">Days</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {jotformData
                            .sort((a, b) => (b.totalBonus || 0) - (a.totalBonus || 0))
                            .map((employee) => (
                            <React.Fragment key={employee.id}>
                              <TableRow 
                                className="cursor-pointer hover:bg-slate-50"
                                onClick={() => {
                                  setExpandedJotformRow(prev => prev === employee.id ? null : employee.id);
                                }}
                                data-testid={`jotform-row-${employee.id}`}
                              >
                                <TableCell className="font-medium">{employee.name}</TableCell>
                                <TableCell>
                                  <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                                    employee.role === 'Doctor' ? 'bg-blue-100 text-blue-700' :
                                    employee.role === 'Nurse' ? 'bg-green-100 text-green-700' :
                                    'bg-slate-100 text-slate-700'
                                  }`}>
                                    {employee.role}
                                  </span>
                                </TableCell>
                                <TableCell>{employee.location || '—'}</TableCell>
                                <TableCell className="text-right font-semibold text-green-700">
                                  ${(employee.totalBonus || 0).toFixed(2)}
                                </TableCell>
                                <TableCell className="text-center">
                                  <span className="flex items-center justify-center gap-1">
                                    {employee.bonuses?.length || 0}
                                    <ChevronDown className={`w-3 h-3 transition-transform ${expandedJotformRow === employee.id ? 'rotate-180' : ''}`} />
                                  </span>
                                </TableCell>
                              </TableRow>
                              {expandedJotformRow === employee.id && employee.bonuses && (
                                <TableRow>
                                  <TableCell colSpan={5} className="p-0 bg-slate-50">
                                    <div className="px-6 py-3">
                                      <table className="w-full text-sm">
                                        <thead>
                                          <tr className="text-slate-500">
                                            <th className="text-left py-1 font-medium">Date</th>
                                            <th className="text-left py-1 font-medium">Location</th>
                                            <th className="text-left py-1 font-medium">Reason</th>
                                            <th className="text-right py-1 font-medium">Amount</th>
                                          </tr>
                                        </thead>
                                        <tbody>
                                          {employee.bonuses
                                            .sort((a, b) => new Date(b.date) - new Date(a.date))
                                            .map((bonus, idx) => (
                                            <tr key={idx} className="border-t border-slate-200">
                                              <td className="py-1.5">{bonus.date}</td>
                                              <td className="py-1.5">{bonus.location || '—'}</td>
                                              <td className="py-1.5 text-slate-600">{bonus.bonusReason}</td>
                                              <td className="py-1.5 text-right font-medium">
                                                ${(bonus.amount || 0).toFixed(2)}
                                              </td>
                                            </tr>
                                          ))}
                                        </tbody>
                                      </table>
                                    </div>
                                  </TableCell>
                                </TableRow>
                              )}
                            </React.Fragment>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Closings History Tab */}
          <TabsContent value="closings-history" className="space-y-5" data-testid="closings-history-content">
            <div className="flex flex-col gap-3 border-b border-slate-200 pb-4 sm:flex-row sm:items-end sm:justify-between">
              <div>
                <div className="mb-1 flex items-center gap-2 text-[11px] font-medium uppercase tracking-wider text-slate-400">
                  <span>Reports</span><ChevronRight className="h-3 w-3" /><span className="text-slate-500">Closings History</span>
                </div>
                <h1 className="text-2xl font-semibold tracking-tight text-slate-900">Closings History</h1>
                <p className="mt-1 text-sm text-slate-500">All daily closings grouped by bi-weekly pay period. Click a row to expand.</p>
              </div>
              <div>
                <Label className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">Location</Label>
                <Select value={selectedLocation} onValueChange={setSelectedLocation}>
                  <SelectTrigger className="mt-1 w-48 rounded-sm border-slate-300 text-sm" data-testid="history-location-filter">
                    <SelectValue placeholder="All Locations" />
                  </SelectTrigger>
                  <SelectContent className="bg-white">
                    <SelectItem value="all">All Locations</SelectItem>
                    {locations.map((loc) => (<SelectItem key={loc.id} value={loc.id}>{loc.name}</SelectItem>))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {historyLoading ? (
              <div className="flex items-center justify-center h-64"><RefreshCw className="w-8 h-8 animate-spin text-[#0073BB]" /></div>
            ) : (historyData && historyData.length > 0) ? (() => {
              const fmtRange = (s, e) => {
                const ds = new Date(s + 'T00:00:00'); const de = new Date(e + 'T00:00:00');
                const opts = { month: 'short', day: 'numeric' };
                return `${ds.toLocaleDateString('en-US', opts)} – ${de.toLocaleDateString('en-US', { ...opts, year: 'numeric' })}`;
              };
              const idx = Math.min(historyPeriodIndex, historyData.length - 1);
              const period = historyData[idx];
              return (
              <div className="space-y-4">
                {/* Period navigator */}
                <div className="flex items-center justify-between rounded-md border border-slate-200 bg-white px-3 py-2 shadow-sm">
                  <Button type="button" variant="ghost" size="sm" className="rounded-sm text-slate-600 hover:bg-slate-100 disabled:opacity-40" onClick={() => { setHistoryPeriodIndex(i => Math.min(i + 1, historyData.length - 1)); setExpandedClosing(null); }} disabled={idx >= historyData.length - 1} data-testid="history-prev-period">
                    <ChevronLeft className="mr-1 h-4 w-4" /> Older
                  </Button>
                  <div className="flex items-center gap-2">
                    <span className="font-mono text-sm font-semibold text-slate-800" data-testid="history-current-period">{fmtRange(period.period_start, period.period_end)}</span>
                    {period.is_current && <span className="inline-flex items-center rounded-full bg-blue-100 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-blue-700">Current</span>}
                  </div>
                  <Button type="button" variant="ghost" size="sm" className="rounded-sm text-slate-600 hover:bg-slate-100 disabled:opacity-40" onClick={() => { setHistoryPeriodIndex(i => Math.max(i - 1, 0)); setExpandedClosing(null); }} disabled={idx <= 0} data-testid="history-next-period">
                    Newer <ChevronRight className="ml-1 h-4 w-4" />
                  </Button>
                </div>
                {(() => {
                  return (
                    <div key={period.period_start} className="overflow-hidden rounded-md border border-slate-200 bg-white shadow-sm" data-testid={`history-period-${period.period_start}`}>
                      <div className="flex flex-col gap-2 border-b border-slate-200 bg-slate-50/70 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-slate-400">{period.totals.count} closing{period.totals.count === 1 ? '' : 's'} · {period.totals.finalized_count} finalized</span>
                        </div>
                        <div className="flex items-center gap-5 text-sm">
                          <div className="text-right"><p className="text-[10px] uppercase tracking-wider text-slate-400">Collected</p><p className="font-mono font-bold tabular-nums text-slate-900">{formatCurrency(period.totals.total_collected)}</p></div>
                          <div className="text-right"><p className="text-[10px] uppercase tracking-wider text-slate-400">Net Bonus</p><p className="font-mono font-bold tabular-nums text-emerald-700">{formatCurrency(period.totals.total_net_bonus)}</p></div>
                        </div>
                      </div>

                      {period.closings.length === 0 ? (
                        <p className="px-4 py-6 text-center text-sm text-slate-400">No closings in this period.</p>
                      ) : (
                        <div className="divide-y divide-slate-100">
                          {period.closings.map((c) => {
                            const rowKey = `${c.date}-${c.location_id || 'all'}`;
                            const isOpen = expandedClosing === rowKey;
                            const comp = c.computed || {}; const adj = c.adjusted || {}; const ptv = adj.payment_totals || {};
                            const prettyDate = new Date(c.date + 'T00:00:00').toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
                            return (
                              <div key={rowKey} data-testid={`history-row-${rowKey}`}>
                                <button
                                  onClick={() => setExpandedClosing(isOpen ? null : rowKey)}
                                  className="flex w-full items-center gap-3 px-4 py-3 text-left transition-colors hover:bg-slate-50"
                                  data-testid={`history-row-toggle-${rowKey}`}
                                >
                                  <ChevronRight className={`h-4 w-4 flex-shrink-0 text-slate-400 transition-transform ${isOpen ? 'rotate-90' : ''}`} />
                                  <span className="w-32 font-mono text-sm font-medium text-slate-900">{prettyDate}</span>
                                  <span className="hidden w-40 truncate text-sm text-slate-500 sm:inline">{c.location_name}</span>
                                  <span className="hidden flex-1 text-sm sm:inline"><span className="font-mono tabular-nums text-slate-700">{formatCurrency(comp.total_collected)}</span></span>
                                  <span className="hidden w-28 text-right text-sm sm:inline"><span className="font-mono tabular-nums text-emerald-700">{formatCurrency(comp.total_bonus_net)}</span></span>
                                  {c.status === 'finalized' ? (
                                    <span className="ml-auto inline-flex items-center gap-1 rounded-sm border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-[11px] font-semibold text-emerald-800 sm:ml-0"><Lock className="h-3 w-3" /> Finalized</span>
                                  ) : (
                                    <span className="ml-auto inline-flex items-center gap-1 rounded-sm border border-amber-200 bg-amber-50 px-2 py-0.5 text-[11px] font-semibold text-amber-800 sm:ml-0"><Edit3 className="h-3 w-3" /> Draft</span>
                                  )}
                                </button>

                                {isOpen && (
                                  <div className="space-y-4 bg-slate-50/50 px-4 pb-5 pt-2" data-testid={`history-detail-${rowKey}`}>
                                    {/* KPIs */}
                                    <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                                      {[
                                        { l: 'Total Collected', v: formatCurrency(comp.total_collected), c: 'text-slate-900', t: '#0073BB' },
                                        { l: 'Total Cash', v: formatCurrency(ptv.cash), c: 'text-slate-900', t: '#cbd5e1' },
                                        { l: 'Total Credit Card', v: formatCurrency(ptv.credit_card), c: 'text-slate-900', t: '#cbd5e1' },
                                        { l: 'Total Net Bonus', v: formatCurrency(comp.total_bonus_net), c: 'text-emerald-700', t: '#10b981' },
                                      ].map((k) => (
                                        <div key={k.l} className="rounded-sm border border-slate-200 bg-white px-3 py-2.5" style={{ borderTop: `2px solid ${k.t}` }}>
                                          <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">{k.l}</p>
                                          <p className={`font-mono text-base font-bold tabular-nums ${k.c}`}>{k.v}</p>
                                        </div>
                                      ))}
                                    </div>

                                    {/* Payment + Service */}
                                    <div className="grid gap-3 sm:grid-cols-2">
                                      <div className="rounded-sm border border-slate-200 bg-white p-3">
                                        <p className="mb-2 text-[11px] font-semibold uppercase tracking-wider text-slate-500">Payment Breakdown</p>
                                        <div className="space-y-1 text-sm">
                                          {[['Cash', ptv.cash], ['Credit Card', ptv.credit_card], ['Zelle / CashApp', ptv.zelle_cashapp], ['Other', ptv.other]].map(([l, v]) => (
                                            <div key={l} className="flex justify-between border-b border-slate-50 py-1"><span className="text-slate-500">{l}</span><span className="font-mono tabular-nums text-slate-800">{formatCurrency(v)}</span></div>
                                          ))}
                                          <div className="flex justify-between pt-1 font-bold"><span>Total Collected</span><span className="font-mono tabular-nums">{formatCurrency(comp.total_collected)}</span></div>
                                        </div>
                                      </div>
                                      <div className="rounded-sm border border-slate-200 bg-white p-3">
                                        <p className="mb-2 text-[11px] font-semibold uppercase tracking-wider text-slate-500">Service & Bonus</p>
                                        <div className="space-y-1 text-sm">
                                          {[['EKG Count', adj.ekg_count ?? 0], ['Ultrasound Count', adj.us_count ?? 0], ['Bonus Tier', comp.bonus_tier || 'None'], ['Base + EKG / emp', formatCurrency(comp.per_employee_common)], ['Total Gross Bonus', formatCurrency(comp.total_bonus)]].map(([l, v]) => (
                                            <div key={l} className="flex justify-between border-b border-slate-50 py-1"><span className="text-slate-500">{l}</span><span className="font-mono tabular-nums text-slate-800">{v}</span></div>
                                          ))}
                                        </div>
                                      </div>
                                    </div>

                                    {/* Visit Counts */}
                                    {adj.visit_counts && (
                                      <div className="rounded-sm border border-slate-200 bg-white p-3" data-testid={`history-visit-counts-${rowKey}`}>
                                        <p className="mb-2 text-[11px] font-semibold uppercase tracking-wider text-slate-500">Visit Counts</p>
                                        <div className="grid grid-cols-5 gap-2 text-center">
                                          {[
                                            { k: 'np', label: 'NP', color: 'text-emerald-600', top: '#10b981' },
                                            { k: 'nc', label: 'NC', color: 'text-violet-600', top: '#8b5cf6' },
                                            { k: 'fu', label: 'FU', color: 'text-[#0073BB]', top: '#0073BB' },
                                            { k: 'lr', label: 'LR', color: 'text-amber-600', top: '#f59e0b' },
                                            { k: 'in', label: 'IN', color: 'text-rose-600', top: '#f43f5e' },
                                          ].map(({ k, label, color, top }) => (
                                            <div key={k} className="rounded-sm border border-slate-200 px-1 py-1.5" style={{ borderTop: `2px solid ${top}` }}>
                                              <p className={`font-mono text-lg font-bold tabular-nums ${color}`}>{adj.visit_counts?.[k] ?? 0}</p>
                                              <p className={`text-[10px] font-bold ${color}`}>{label}</p>
                                            </div>
                                          ))}
                                        </div>
                                      </div>
                                    )}

                                    {/* Employees */}
                                    {comp.employee_bonuses?.length ? (
                                      <div className="overflow-x-auto rounded-sm border border-slate-200 bg-white">
                                        <table className="w-full text-sm">
                                          <thead><tr className="bg-slate-50 text-left text-[11px] uppercase tracking-wider text-slate-600"><th className="px-3 py-2 font-semibold">Employee</th><th className="px-3 py-2 font-semibold">Role</th><th className="px-3 py-2 font-semibold">On Shift</th><th className="px-3 py-2 font-semibold">US</th><th className="px-3 py-2 text-right font-semibold">Gross</th><th className="px-3 py-2 text-right font-semibold">Net</th></tr></thead>
                                          <tbody>
                                            {comp.employee_bonuses.map((e) => (
                                              <tr key={e.id} className="border-t border-slate-100 text-slate-700">
                                                <td className="px-3 py-1.5 font-medium text-slate-900">{e.full_name}</td>
                                                <td className="px-3 py-1.5 capitalize text-slate-500">{e.role?.replace('_', ' ')}</td>
                                                <td className="px-3 py-1.5">{e.on_shift ? <span className="font-medium text-emerald-600">Yes</span> : <span className="text-slate-400">Indication only</span>}</td>
                                                <td className="px-3 py-1.5 font-mono tabular-nums">{e.us_indicated > 0 ? e.us_indicated : <span className="text-slate-300">—</span>}</td>
                                                <td className="px-3 py-1.5 text-right font-mono font-semibold tabular-nums text-slate-900">{formatCurrency(e.gross_bonus)}</td>
                                                <td className="px-3 py-1.5 text-right font-mono font-semibold tabular-nums text-emerald-700">{formatCurrency(e.net_bonus)}</td>
                                              </tr>
                                            ))}
                                          </tbody>
                                          <tfoot><tr className="border-t border-slate-200 bg-slate-50 font-bold"><td className="px-3 py-2">Total ({comp.num_employees})</td><td></td><td></td><td></td><td className="px-3 py-2 text-right font-mono tabular-nums">{formatCurrency(comp.total_bonus)}</td><td className="px-3 py-2 text-right font-mono tabular-nums text-emerald-700">{formatCurrency(comp.total_bonus_net)}</td></tr></tfoot>
                                        </table>
                                      </div>
                                    ) : <p className="text-sm text-slate-400">No employees assigned.</p>}

                                    {/* Attached file */}
                                    {c.attachment && (
                                      <div className="flex flex-wrap items-center justify-between gap-2 rounded-sm border border-slate-200 bg-white p-3" data-testid={`history-attachment-${rowKey}`}>
                                        <div className="flex min-w-0 items-center gap-2">
                                          <Paperclip className="h-4 w-4 flex-shrink-0 text-slate-400" />
                                          <span className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">Attached File</span>
                                          <span className="truncate text-sm font-medium text-slate-800">{c.attachment.filename}</span>
                                        </div>
                                        <Button type="button" variant="outline" size="sm" className="rounded-sm border-slate-300" onClick={() => openHistoryAttachment(c.date, c.location_id)} data-testid={`view-history-attachment-${rowKey}`}>
                                          <ExternalLink className="mr-1.5 h-3.5 w-3.5" /> View
                                        </Button>
                                      </div>
                                    )}

                                    <p className="text-xs text-slate-400">
                                      {c.status === 'finalized' && c.finalized_by_name ? `Finalized by ${c.finalized_by_name}` : c.updated_by_name ? `Last updated by ${c.updated_by_name}` : ''}
                                    </p>
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })()}
              </div>
              );
            })() : (
              <div className="flex flex-col items-center justify-center gap-2 py-16 text-slate-400">
                <ClipboardList className="h-10 w-10 text-slate-300" />
                <p>No closings found for the recent bi-weekly periods.</p>
              </div>
            )}
          </TabsContent>

          {/* Employee Bonuses Tab */}
          <TabsContent value="employee-bonuses" className="space-y-5" data-testid="employee-bonuses-content">
            <div className="flex flex-col gap-3 border-b border-slate-200 pb-4 sm:flex-row sm:items-end sm:justify-between">
              <div>
                <div className="mb-1 flex items-center gap-2 text-[11px] font-medium uppercase tracking-wider text-slate-400">
                  <span>Reports</span><ChevronRight className="h-3 w-3" /><span className="text-slate-500">Employee Bonuses</span>
                </div>
                <h1 className="text-2xl font-semibold tracking-tight text-slate-900">Employee Bonuses</h1>
                <p className="mt-1 text-sm text-slate-500">Bonus earned by each employee across the 14 days of the bi-weekly pay period (finalized closings only).</p>
              </div>
              <div className="flex items-end gap-3">
                <div>
                  <Label className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">Location</Label>
                  <Select value={selectedLocation} onValueChange={setSelectedLocation}>
                    <SelectTrigger className="mt-1 w-48 rounded-sm border-slate-300 text-sm" data-testid="emp-bonus-location-filter">
                      <SelectValue placeholder="All Locations" />
                    </SelectTrigger>
                    <SelectContent className="bg-white">
                      <SelectItem value="all">All Locations</SelectItem>
                      {locations.map((loc) => (<SelectItem key={loc.id} value={loc.id}>{loc.name}</SelectItem>))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            {empBonusLoading ? (
              <div className="flex items-center justify-center h-64"><RefreshCw className="w-8 h-8 animate-spin text-[#0073BB]" /></div>
            ) : empBonusData ? (() => {
              const fmtRange = (s, e) => {
                const ds = new Date(s + 'T00:00:00'); const de = new Date(e + 'T00:00:00');
                const opts = { month: 'short', day: 'numeric' };
                return `${ds.toLocaleDateString('en-US', opts)} – ${de.toLocaleDateString('en-US', { ...opts, year: 'numeric' })}`;
              };
              const key = empBonusView; // 'net' | 'gross'
              const dayCell = (d, dd) => (dd && dd[key] ? formatCurrency(dd[key]) : <span className="text-slate-300">—</span>);
              return (
                <div className="space-y-4">
                  {/* Period navigator */}
                  <div className="flex items-center justify-between rounded-md border border-slate-200 bg-white px-3 py-2 shadow-sm">
                    <Button type="button" variant="ghost" size="sm" className="rounded-sm text-slate-600 hover:bg-slate-100 disabled:opacity-40" onClick={() => setEmpBonusPeriod(p => p + 1)} data-testid="emp-bonus-prev-period">
                      <ChevronLeft className="mr-1 h-4 w-4" /> Older
                    </Button>
                    <div className="flex items-center gap-2">
                      <span className="font-mono text-sm font-semibold text-slate-800" data-testid="emp-bonus-current-period">{fmtRange(empBonusData.period_start, empBonusData.period_end)}</span>
                      {empBonusData.is_current && <span className="inline-flex items-center rounded-full bg-blue-100 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-blue-700">Current</span>}
                    </div>
                    <Button type="button" variant="ghost" size="sm" className="rounded-sm text-slate-600 hover:bg-slate-100 disabled:opacity-40" onClick={() => setEmpBonusPeriod(p => Math.max(p - 1, 0))} disabled={!empBonusData.has_newer} data-testid="emp-bonus-next-period">
                      Newer <ChevronRight className="ml-1 h-4 w-4" />
                    </Button>
                  </div>

                  {empBonusData.employees && empBonusData.employees.length > 0 ? (
                    <>
                      {/* Bulk payslip actions */}
                      <div className="flex flex-wrap items-center justify-end gap-2">
                        <Button type="button" variant="outline" size="icon" className="h-9 w-9 rounded-sm border-slate-300 text-slate-700 hover:bg-slate-50" title="Download all bonuses (PDF)" onClick={handleDownloadAllPayslips} data-testid="emp-bonus-download-all">
                          <Download className="h-4 w-4" />
                        </Button>
                        <Button type="button" size="sm" className="rounded-sm bg-[#0972d3] text-white hover:bg-[#0761b8]" onClick={handleEmailAllPayslips} data-testid="emp-bonus-email-all">
                          <Mail className="mr-1.5 h-4 w-4" /> Email All Bonuses
                        </Button>
                      </div>
                      <div className="overflow-x-auto rounded-md border border-slate-200 bg-white shadow-sm">
                      <table className="w-full border-collapse text-sm" data-testid="emp-bonus-table">
                        <thead>
                          <tr className="bg-slate-50/80">
                            <th className="sticky left-0 z-10 whitespace-nowrap border-b border-slate-200 bg-slate-50/80 px-3 py-2.5 text-left text-[11px] font-semibold uppercase tracking-wider text-slate-500">Employee</th>
                            {empBonusData.days.map((d) => {
                              const dt = new Date(d + 'T00:00:00');
                              return (
                                <th key={d} className="border-b border-slate-200 px-2 py-2.5 text-center text-[10px] font-semibold uppercase tracking-wide text-slate-500">
                                  <div>{dt.toLocaleDateString('en-US', { weekday: 'short' })}</div>
                                  <div className="font-mono text-slate-700">{dt.toLocaleDateString('en-US', { month: 'numeric', day: 'numeric' })}</div>
                                </th>
                              );
                            })}
                            <th className="whitespace-nowrap border-b border-l border-slate-200 bg-slate-50 px-3 py-2.5 text-right text-[11px] font-semibold uppercase tracking-wider text-slate-600">Period Total</th>
                          </tr>
                        </thead>
                        <tbody>
                          {empBonusData.employees.map((emp) => {
                            const empKey = emp.employee_id || emp.name;
                            const isExpanded = expandedEmpBonus === empKey;
                            const activeDays = Object.keys(emp.days || {}).sort();
                            return (
                            <React.Fragment key={empKey}>
                            <tr className="cursor-pointer hover:bg-slate-50/60" data-testid={`emp-bonus-row-${empKey}`} onClick={() => setExpandedEmpBonus(isExpanded ? null : empKey)}>
                              <td className={`sticky left-0 z-10 whitespace-nowrap px-3 py-2.5 font-medium text-slate-900 ${isExpanded ? 'border-l-2 border-t-2 border-[#0972d3] bg-blue-50' : 'border-b border-slate-100 bg-white'}`}>
                                <span className="inline-flex items-center gap-1.5">
                                  <ChevronRight className={`h-3.5 w-3.5 flex-shrink-0 text-slate-400 transition-transform ${isExpanded ? 'rotate-90' : ''}`} />
                                  {emp.name}
                                </span>
                              </td>
                              {empBonusData.days.map((d) => {
                                const dd = emp.days[d];
                                const hasVal = dd && dd[key];
                                return (
                                  <td key={d} className={`px-2 py-2.5 text-center font-mono text-xs tabular-nums ${isExpanded ? 'border-t-2 border-[#0972d3]' : 'border-b border-slate-100'} ${hasVal ? 'bg-emerald-50 font-semibold text-emerald-800' : (isExpanded ? 'bg-blue-50/50 text-slate-700' : 'text-slate-700')}`}>
                                    {dayCell(d, dd)}
                                  </td>
                                );
                              })}
                              <td className={`px-3 py-2.5 text-right ${isExpanded ? 'border-r-2 border-t-2 border-[#0972d3] bg-blue-50/50' : 'border-b border-l border-slate-100 bg-slate-50/60'}`}>
                                <div className="font-mono text-sm font-bold tabular-nums text-emerald-700" data-testid={`emp-bonus-total-net-${empKey}`}>{formatCurrency(emp.total_net)}<span className="ml-1 text-[9px] font-semibold uppercase text-slate-400">net</span></div>
                                <div className="font-mono text-[11px] tabular-nums text-slate-500">{formatCurrency(emp.total_gross)}<span className="ml-1 text-[9px] font-semibold uppercase text-slate-400">gross</span></div>
                              </td>
                            </tr>
                            {isExpanded && (
                              <tr data-testid={`emp-bonus-detail-${empKey}`}>
                                <td colSpan={empBonusData.days.length + 2} className="border-x-2 border-b-2 border-[#0972d3] bg-blue-50/50 px-4 py-4">
                                  <div className="rounded-md border border-slate-200 bg-white p-4 shadow-sm">
                                    <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
                                      <div>
                                        <p className="text-sm font-semibold text-slate-900">{emp.name} <span className="ml-1 text-xs font-normal text-slate-500">{(emp.role || '').replace(/_/g, ' ')}</span></p>
                                        <p className="text-xs text-slate-500">{emp.email || 'No email on file'}</p>
                                      </div>
                                    </div>
                                    <div className="overflow-x-auto">
                                      <table className="w-full border-collapse text-sm">
                                        <thead>
                                          <tr className="bg-slate-50 text-[11px] font-semibold uppercase tracking-wider text-slate-500">
                                            <th className="border-b border-slate-200 px-3 py-2 text-left">Date</th>
                                            <th className="border-b border-slate-200 px-3 py-2 text-left">Location</th>
                                            <th className="border-b border-slate-200 px-3 py-2 text-right">Tier</th>
                                            <th className="border-b border-slate-200 px-3 py-2 text-right">EKG</th>
                                            <th className="border-b border-slate-200 px-3 py-2 text-right">Ultrasound</th>
                                            <th className="border-b border-slate-200 px-3 py-2 text-right">Gross</th>
                                            <th className="border-b border-slate-200 px-3 py-2 text-right">Net</th>
                                          </tr>
                                        </thead>
                                        <tbody>
                                          {activeDays.length === 0 ? (
                                            <tr><td colSpan={7} className="px-3 py-4 text-center text-sm text-slate-400">No bonus days in this period.</td></tr>
                                          ) : activeDays.map((d) => {
                                            const dd = emp.days[d];
                                            const dt = new Date(d + 'T00:00:00');
                                            return (
                                              <tr key={d} className="hover:bg-slate-50/60">
                                                <td className="border-b border-slate-100 px-3 py-2 font-medium text-slate-800">{dt.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}</td>
                                                <td className="border-b border-slate-100 px-3 py-2 text-slate-600">{dd.location || '—'}</td>
                                                <td className="border-b border-slate-100 px-3 py-2 text-right font-mono tabular-nums text-slate-700">{formatCurrency(dd.tier)}</td>
                                                <td className="border-b border-slate-100 px-3 py-2 text-right font-mono tabular-nums text-slate-700">{formatCurrency(dd.ekg)}</td>
                                                <td className="border-b border-slate-100 px-3 py-2 text-right font-mono tabular-nums text-slate-700">{formatCurrency(dd.us)}</td>
                                                <td className="border-b border-slate-100 px-3 py-2 text-right font-mono tabular-nums text-slate-700">{formatCurrency(dd.gross)}</td>
                                                <td className="border-b border-slate-100 bg-emerald-50 px-3 py-2 text-right font-mono font-bold tabular-nums text-emerald-800">{formatCurrency(dd.net)}</td>
                                              </tr>
                                            );
                                          })}
                                        </tbody>
                                      </table>
                                    </div>
                                    <div className="mt-3 flex items-center gap-1.5">
                                      <Button type="button" variant="ghost" size="icon" className="h-9 w-9 rounded-sm text-[#0972d3] hover:bg-blue-50 hover:text-[#0761b8]" title="Download payslip PDF" onClick={() => handleDownloadPayslip(emp)} data-testid={`emp-bonus-detail-download-${empKey}`}>
                                        <Download className="h-5 w-5" />
                                      </Button>
                                      <Button type="button" variant="ghost" size="icon" disabled={!emp.email} className="h-9 w-9 rounded-sm text-[#0972d3] hover:bg-blue-50 hover:text-[#0761b8] disabled:cursor-not-allowed disabled:opacity-40" title={emp.email ? `Email payslip to ${emp.email}` : 'No email on file'} onClick={() => handleEmailPayslip(emp)} data-testid={`emp-bonus-detail-email-${empKey}`}>
                                        <Mail className="h-5 w-5" />
                                      </Button>
                                    </div>
                                  </div>
                                </td>
                              </tr>
                            )}
                            </React.Fragment>
                            );
                          })}
                        </tbody>
                        <tfoot>
                          <tr className="bg-slate-100/70 font-semibold">
                            <td className="sticky left-0 z-10 whitespace-nowrap border-t border-slate-200 bg-slate-100/70 px-3 py-2.5 text-[11px] font-semibold uppercase tracking-wider text-slate-600">Daily Total</td>
                            {empBonusData.days.map((d) => (
                              <td key={d} className="border-t border-slate-200 px-2 py-2.5 text-center font-mono text-xs tabular-nums text-slate-700">
                                {empBonusData.day_totals[d] && empBonusData.day_totals[d][key] ? formatCurrency(empBonusData.day_totals[d][key]) : <span className="text-slate-300">—</span>}
                              </td>
                            ))}
                            <td className="border-l border-t border-slate-200 bg-slate-100 px-3 py-2.5 text-right" data-testid="emp-bonus-grand-total">
                              <div className="font-mono text-sm font-bold tabular-nums text-emerald-800">{formatCurrency(empBonusData.grand_total.net)}<span className="ml-1 text-[9px] font-semibold uppercase text-slate-500">net</span></div>
                              <div className="font-mono text-[11px] tabular-nums text-slate-600">{formatCurrency(empBonusData.grand_total.gross)}<span className="ml-1 text-[9px] font-semibold uppercase text-slate-500">gross</span></div>
                            </td>
                          </tr>
                        </tfoot>
                      </table>
                      </div>
                    </>
                  ) : (
                    <div className="flex flex-col items-center justify-center gap-2 py-16 text-slate-400">
                      <Users className="h-10 w-10 text-slate-300" />
                      <p>No employee bonuses in this pay period.</p>
                    </div>
                  )}
                </div>
              );
            })() : (
              <div className="flex flex-col items-center justify-center gap-2 py-16 text-slate-400">
                <Users className="h-10 w-10 text-slate-300" />
                <p>No data available.</p>
              </div>
            )}
          </TabsContent>

          </>
        )}

      </Tabs>

      {/* Payslip email confirmation */}
      <AlertDialog open={emailConfirm.open} onOpenChange={(o) => !o && setEmailConfirm({ open: false, mode: null, emp: null })}>
        <AlertDialogContent className="bg-white" data-testid="payslip-email-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Send payslip {emailConfirm.mode === 'all' ? 'to all employees' : 'by email'}?</AlertDialogTitle>
            <AlertDialogDescription>
              {emailConfirm.mode === 'all'
                ? `This will email each employee (with an email on file) their bonus payslip for ${empBonusData?.period_start} to ${empBonusData?.period_end}. Employees without an email will be skipped.`
                : emailConfirm.emp
                  ? `This will email the bonus payslip for ${empBonusData?.period_start} to ${empBonusData?.period_end} to ${emailConfirm.emp.name}${emailConfirm.emp.email ? ` (${emailConfirm.emp.email})` : ''}.`
                  : ''}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="payslip-email-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction className="bg-[#0972d3] text-white hover:bg-[#0761b8]" onClick={confirmSendPayslipEmail} disabled={sendingPayslipEmail} data-testid="payslip-email-confirm">
              {sendingPayslipEmail ? 'Sending…' : 'Send Email'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default Reports;
